import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import cytoscape, { type Core, type EdgeSingular, type ElementDefinition, type NodeSingular } from 'cytoscape'
import { ATLAS_LAYOUT_VERSION, type AtlasLayout, type AtlasRegionId } from '../atlasLayout'
import type { AtlasDisplayEdge, AtlasRelationshipBundle } from '../atlasProjection'
import { atlasRegionTextureSource } from '../atlasRegionTextures'
import type { EdgeType, GraphNode, Locale, NodeType, TimelineTarget, TraditionAccent } from '../data/schema'
import { relationshipColors } from '../graphTokens'
import { copy, localize } from '../i18n'
import type { RelationshipLabelMode } from './FilterPanel'
import type { GraphCanvasHandle } from './GraphCanvas'
import { typeIconDataUri } from './TypeIcon'

interface AtlasCanvasProps {
  nodes: GraphNode[]
  edges: AtlasDisplayEdge[]
  layout: AtlasLayout
  locale: Locale
  focusId?: string
  cameraFollow?: boolean
  cameraFocusTargets?: readonly TimelineTarget[]
  dimmedNodeIds?: ReadonlySet<string>
  labelMode: RelationshipLabelMode
  activeTypes: ReadonlySet<NodeType>
  highlightedType?: NodeType
  relatedTypeFilter?: NodeType
  collectionHighlightIds?: ReadonlySet<string>
  collectionFocusRequest?: number
  traditionAccent?: TraditionAccent
  loadedRegionIds: ReadonlySet<AtlasRegionId>
  loadingRegionIds: ReadonlySet<AtlasRegionId>
  storyContext?: boolean
  onRequestRegion: (id: AtlasRegionId) => void
  onSelectNode: (id: string) => void
  onSelectEdge: (id: string) => void
  onSelectBundle?: (bundle: AtlasRelationshipBundle) => void
  onClearFocus: () => void
}

type AtlasLevel = 'world' | 'region' | 'detail'
type AtlasEdgeSide = 'top' | 'right' | 'bottom' | 'left'
type Point = { x: number; y: number }
type AtlasRect = { x1: number; y1: number; x2: number; y2: number }

interface AtlasFocusNeighbor {
  id: string
  position: Point
}

interface AtlasLabelCandidate {
  textHalign: 'left' | 'center' | 'right'
  textValign: 'top' | 'center' | 'bottom'
  textJustification: 'left' | 'center' | 'right'
  offsetX: number
  offsetY: number
}

interface AtlasOffscreenLink {
  nodeId: string
  label: string
  relationship: string
}

interface AtlasOffscreenGroup {
  side: AtlasEdgeSide
  x: number
  y: number
  items: AtlasOffscreenLink[]
}

interface AtlasStoredLabelPlacement {
  focusId: string
  textHalign: string
  textValign: string
  textJustification: string
  offsetX: number
  offsetY: number
}

interface AtlasMaterializationOptions {
  extent: AtlasRect
  level: AtlasLevel
  focusId?: string
  focusTargets?: readonly TimelineTarget[]
  highlightedType?: NodeType
  collectionNodeIds?: ReadonlySet<string>
}

const timelineTargetKey = (target: TimelineTarget) => (
  target.kind === 'node' ? `node:${target.nodeId}` : `edge:${target.edgeId}`
)

const clamp01 = (value: number) => Math.max(0, Math.min(1, value))
const progressBetween = (value: number, start: number, end: number) => clamp01((value - start) / (end - start))
const interpolate = (start: number, end: number, progress: number) => start + (end - start) * progress
export const DEFAULT_ATLAS_ZOOM_RATIO = 1.42
const ATLAS_DETAIL_ZOOM_RATIO = 4.1
const FOCUS_OVERVIEW_DESKTOP_PADDING = 116
const FOCUS_OVERVIEW_TABLET_PADDING = 88
const FOCUS_OVERVIEW_MOBILE_NEIGHBOR_LIMIT = 8
const VIEWPORT_PRESENTATION_INTERVAL_MS = 32
const FOCUS_LABEL_LAYOUT_DELAY_MS = 140
const ATLAS_NODE_OVERSCAN_RATIO = 0.16
const TIMELINE_DENSE_LABEL_NODE_THRESHOLD = 6
const ATLAS_REGION_TINT_OPACITY = 0.1
const ATLAS_REGION_TEXTURE_OPACITY = 0.52
const ATLAS_REGION_BORDER_WIDTH = 1.15
const ATLAS_REGION_BORDER_OPACITY = 0.3

export type AtlasFocusOverviewRole = 'focus' | 'neighbor'

export function atlasFocusOverviewMetrics(role: AtlasFocusOverviewRole, absoluteZoom: number, mobile = false) {
  const zoom = Math.max(absoluteZoom, 0.001)
  const renderedSize = role === 'focus' ? mobile ? 64 : 74 : mobile ? 36 : 40
  const renderedFontSize = role === 'focus' ? mobile ? 12 : 13 : mobile ? 10 : 11
  const renderedLabelWidth = role === 'focus' ? mobile ? 136 : 156 : mobile ? 112 : 128
  const renderedBorderWidth = role === 'focus' ? 4 : 2.5
  return {
    size: renderedSize / zoom,
    fontSize: renderedFontSize / zoom,
    labelWidth: renderedLabelWidth / zoom,
    labelMargin: (role === 'focus' ? 11 : 9) / zoom,
    outlineWidth: (role === 'focus' ? 3.2 : 2.8) / zoom,
    borderWidth: renderedBorderWidth / zoom,
    renderedSize,
    renderedFontSize,
  }
}

export function atlasTimelineFocusMetrics(absoluteZoom: number, mobile = false) {
  const zoom = Math.max(absoluteZoom, 0.001)
  const renderedSize = mobile ? 42 : 46
  const renderedFontSize = mobile ? 10.5 : 11.5
  const renderedLabelWidth = mobile ? 124 : 142
  return {
    size: renderedSize / zoom,
    fontSize: renderedFontSize / zoom,
    labelWidth: renderedLabelWidth / zoom,
    labelMargin: 9 / zoom,
    outlineWidth: 2.8 / zoom,
    borderWidth: 2.8 / zoom,
    renderedSize,
    renderedFontSize,
  }
}

export function atlasCollectionSpotlightMetrics(absoluteZoom: number, mobile = false) {
  const zoom = Math.max(absoluteZoom, 0.001)
  const renderedSize = mobile ? 42 : 46
  const renderedFontSize = mobile ? 10.5 : 11.5
  const renderedLabelWidth = mobile ? 124 : 142
  return {
    size: renderedSize / zoom,
    fontSize: renderedFontSize / zoom,
    labelWidth: renderedLabelWidth / zoom,
    labelMargin: 9 / zoom,
    outlineWidth: 2.8 / zoom,
    borderWidth: 3.4 / zoom,
    renderedSize,
    renderedFontSize,
  }
}

export function atlasRegionHeadingMetrics(absoluteZoom: number, mobile = false) {
  const zoom = Math.max(absoluteZoom, 0.001)
  const renderedFontSize = mobile ? 11.5 : 13
  const renderedMaxWidth = mobile ? 176 : 224
  return {
    fontSize: renderedFontSize / zoom,
    labelWidth: renderedMaxWidth / zoom,
    labelMargin: -14 / zoom,
    outlineWidth: 2.8 / zoom,
    backgroundPadding: 3 / zoom,
    renderedFontSize,
    renderedMaxWidth,
  }
}

export function atlasFocusOverviewPadding(viewportWidth: number) {
  return viewportWidth < 1180 ? FOCUS_OVERVIEW_TABLET_PADDING : FOCUS_OVERVIEW_DESKTOP_PADDING
}

export function atlasFocusOverviewNeighborIds(origin: Point, neighbors: AtlasFocusNeighbor[], mobile: boolean) {
  const ranked = [...neighbors].sort((left, right) => {
    const leftDistance = Math.hypot(left.position.x - origin.x, left.position.y - origin.y)
    const rightDistance = Math.hypot(right.position.x - origin.x, right.position.y - origin.y)
    return leftDistance - rightDistance || left.id.localeCompare(right.id)
  })
  return (mobile ? ranked.slice(0, FOCUS_OVERVIEW_MOBILE_NEIGHBOR_LIMIT) : ranked).map((neighbor) => neighbor.id)
}

export function atlasProjectedLabelBox(
  nodeBox: AtlasRect,
  labelSize: { width: number; height: number },
  candidate: Pick<AtlasLabelCandidate, 'textHalign' | 'textValign' | 'offsetX' | 'offsetY'>,
): AtlasRect {
  const centerX = (nodeBox.x1 + nodeBox.x2) / 2
  const centerY = (nodeBox.y1 + nodeBox.y2) / 2
  const x1 = candidate.textHalign === 'right'
    ? nodeBox.x2 + candidate.offsetX
    : candidate.textHalign === 'left'
      ? nodeBox.x1 + candidate.offsetX - labelSize.width
      : centerX + candidate.offsetX - labelSize.width / 2
  const y1 = candidate.textValign === 'bottom'
    ? nodeBox.y2 + candidate.offsetY
    : candidate.textValign === 'top'
      ? nodeBox.y1 + candidate.offsetY - labelSize.height
      : centerY + candidate.offsetY - labelSize.height / 2
  return { x1, y1, x2: x1 + labelSize.width, y2: y1 + labelSize.height }
}

export function atlasMaterializedElementIds(
  layout: AtlasLayout,
  nodes: ReadonlyArray<Pick<GraphNode, 'id' | 'type'>>,
  edges: ReadonlyArray<Pick<AtlasDisplayEdge, 'id' | 'source' | 'target'>>,
  options: AtlasMaterializationOptions,
) {
  const width = Math.max(1, options.extent.x2 - options.extent.x1)
  const height = Math.max(1, options.extent.y2 - options.extent.y1)
  const overscanX = width * ATLAS_NODE_OVERSCAN_RATIO
  const overscanY = height * ATLAS_NODE_OVERSCAN_RATIO
  const insideViewport = (point: Point) => point.x >= options.extent.x1 - overscanX
    && point.x <= options.extent.x2 + overscanX
    && point.y >= options.extent.y1 - overscanY
    && point.y <= options.extent.y2 + overscanY
  const focusNodeIds = new Set<string>()
  if (options.focusId) {
    const focusedEdge = edges.find((edge) => edge.id === options.focusId)
    if (focusedEdge) {
      focusNodeIds.add(focusedEdge.source)
      focusNodeIds.add(focusedEdge.target)
    } else {
      focusNodeIds.add(options.focusId)
      edges.forEach((edge) => {
        if (edge.source === options.focusId) focusNodeIds.add(edge.target)
        else if (edge.target === options.focusId) focusNodeIds.add(edge.source)
      })
    }
  }
  options.focusTargets?.forEach((target) => {
    if (target.kind === 'node') {
      focusNodeIds.add(target.nodeId)
      return
    }
    const focusedEdge = edges.find((edge) => edge.id === target.edgeId)
    if (!focusedEdge) return
    focusNodeIds.add(focusedEdge.source)
    focusNodeIds.add(focusedEdge.target)
  })
  const nodeById = new Map(nodes.map((node) => [node.id, node]))
  const canonicalNodeIds = new Set<string>()
  layout.placements.forEach((placement) => {
    const node = nodeById.get(placement.id)
    if (!node) return
    const inViewport = insideViewport(placement)
    const semanticLayerVisible = placement.displayTier === 0
      || (options.level === 'region' && placement.displayTier <= 1 && inViewport)
      || (options.level === 'detail' && inViewport)
    const highlightedEarlier = Boolean(options.highlightedType === node.type && inViewport)
    if (semanticLayerVisible || highlightedEarlier || focusNodeIds.has(placement.id) || options.collectionNodeIds?.has(placement.id)) canonicalNodeIds.add(placement.id)
  })
  const reservationNodeIds = new Set<string>()
  if (options.level === 'detail') layout.reservations.forEach((reservation) => {
    if (insideViewport(reservation)) reservationNodeIds.add(`atlas-reservation-${reservation.id}`)
  })
  return { canonicalNodeIds, reservationNodeIds, focusNodeIds }
}

const colors: Record<NodeType, string> = {
  deity: '#D6A647', hero: '#4D8FCC', mortal: '#CDAE79', creature: '#CF6F5C', place: '#4F9A78', event: '#8C69A8', artifact: '#C58A45',
}

const shapes = {
  deity: 'ellipse', hero: 'round-rectangle', mortal: 'round-rectangle', creature: 'octagon', place: 'diamond', event: 'hexagon', artifact: 'barrel',
} as const

export function atlasLevelForZoomRatio(ratio: number): AtlasLevel {
  if (ratio < 1.18) return 'world'
  if (ratio < 2.15) return 'region'
  return 'detail'
}

export function atlasLayerProgressForZoom(ratio: number) {
  return {
    featured: progressBetween(ratio, 1.04, 1.5),
    detail: progressBetween(ratio, 1.42, 2.28),
    featuredLabel: progressBetween(ratio, 1.04, 1.5),
    detailLabel: progressBetween(ratio, 1.42, 2.28),
    region: 1 - progressBetween(ratio, 1.35, 2.55) * 0.68,
  }
}

export function atlasRelationshipProgressForZoom(ratio: number) {
  return progressBetween(ratio, 1.22, 2.24)
}

export function atlasTypeHighlightProgress(type: NodeType, highlightedType: NodeType | undefined, isReservation: boolean, ratio: number) {
  const matches = Boolean(highlightedType && type === highlightedType && !isReservation)
  return {
    matches,
    contextOpacity: highlightedType ? matches ? 1 : isReservation ? 0.1 : 0.24 : 1,
    visibilityFloor: matches ? progressBetween(ratio, 0.88, 1.42) : 0,
    labelFloor: matches ? progressBetween(ratio, 1.12, 1.78) : 0,
  }
}

export function projectAtlasEdgeLink(origin: Point, target: Point, width: number, height: number, inset = 30) {
  const dx = target.x - origin.x
  const dy = target.y - origin.y
  const candidates: Array<{ scale: number; side: AtlasEdgeSide }> = []
  if (dx > 0) candidates.push({ scale: (width - inset - origin.x) / dx, side: 'right' })
  if (dx < 0) candidates.push({ scale: (inset - origin.x) / dx, side: 'left' })
  if (dy > 0) candidates.push({ scale: (height - inset - origin.y) / dy, side: 'bottom' })
  if (dy < 0) candidates.push({ scale: (inset - origin.y) / dy, side: 'top' })
  const hit = candidates.filter((candidate) => candidate.scale >= 0).sort((left, right) => left.scale - right.scale)[0]
  if (!hit) return { side: 'right' as const, x: width - inset, y: height / 2 }
  return {
    side: hit.side,
    x: Math.max(inset, Math.min(width - inset, origin.x + dx * hit.scale)),
    y: Math.max(inset, Math.min(height - inset, origin.y + dy * hit.scale)),
  }
}

export function atlasFocusLabelPlacement(origin: Point, target: Point, absoluteZoom: number) {
  const zoom = Math.max(absoluteZoom, 0.001)
  const dx = target.x - origin.x
  const dy = target.y - origin.y
  const margin = 10 / zoom
  if (Math.abs(dx) > Math.abs(dy) * 0.82) {
    const right = dx >= 0
    return {
      textHalign: right ? 'right' : 'left',
      textValign: 'center',
      textJustification: right ? 'left' : 'right',
      marginX: right ? margin : -margin,
      marginY: 0,
    }
  }
  const below = dy >= 0
  return {
    textHalign: 'center',
    textValign: below ? 'bottom' : 'top',
    textJustification: 'center',
    marginX: 0,
    marginY: below ? margin : -margin,
  }
}

function localizedAtlasLabel(node: GraphNode, locale: Locale) {
  const primary = localize(node.name, locale)
  return locale === 'en' || primary === node.name.en ? primary : `${primary}\n${node.name.en}`
}

export function localizedAtlasRegionLabel(label: AtlasLayout['regions'][number]['label'], locale: Locale) {
  return localize(label, locale)
}

export function restoreAtlasEdgeAfterHover(
  edge: EdgeSingular,
  refreshPresentation: () => void,
  scheduleReset: (callback: () => void) => void = (callback) => { window.requestAnimationFrame(callback) },
) {
  edge.style('transition-duration', '0ms')
  edge.removeClass('atlas-edge-hovered')
  refreshPresentation()
  // Cytoscape can otherwise retain the end of the hover opacity transition
  // until the next viewport event. Restore normal transitions on the next
  // frame, after the semantic-zoom line style has been painted once.
  scheduleReset(() => {
    if (edge.removed()) return
    edge.removeStyle('transition-duration')
    refreshPresentation()
  })
}

function isInsideExtent(node: NodeSingular, extent: { x1: number; y1: number; x2: number; y2: number }) {
  const position = node.position()
  const padding = 80
  return position.x >= extent.x1 - padding && position.x <= extent.x2 + padding
    && position.y >= extent.y1 - padding && position.y <= extent.y2 + padding
}

function edgeIntersectsExtent(edge: EdgeSingular, extent: { x1: number; y1: number; x2: number; y2: number }) {
  const source = edge.source().position()
  const target = edge.target().position()
  const padding = 80
  return Math.max(source.x, target.x) >= extent.x1 - padding
    && Math.min(source.x, target.x) <= extent.x2 + padding
    && Math.max(source.y, target.y) >= extent.y1 - padding
    && Math.min(source.y, target.y) <= extent.y2 + padding
}

export const AtlasCanvas = forwardRef<GraphCanvasHandle, AtlasCanvasProps>(function AtlasCanvas({
  nodes, edges, layout, locale, focusId, cameraFollow = true, cameraFocusTargets, dimmedNodeIds, labelMode, activeTypes, highlightedType, relatedTypeFilter, collectionHighlightIds, collectionFocusRequest = 0, traditionAccent, loadedRegionIds, loadingRegionIds, storyContext = false, onRequestRegion, onSelectNode, onSelectEdge, onSelectBundle, onClearFocus,
}, ref) {
  const containerRef = useRef<HTMLDivElement>(null)
  const cyRef = useRef<Core | null>(null)
  const baseZoomRef = useRef(1)
  const loadedPortraitsRef = useRef(new Set<string>())
  const activeTypesRef = useRef(activeTypes)
  const highlightedTypeRef = useRef(highlightedType)
  const selectNodeRef = useRef(onSelectNode)
  const selectEdgeRef = useRef(onSelectEdge)
  const selectBundleRef = useRef(onSelectBundle)
  const clearFocusRef = useRef(onClearFocus)
  const requestRegionRef = useRef(onRequestRegion)
  const loadedRegionsRef = useRef(loadedRegionIds)
  const loadingRegionsRef = useRef(loadingRegionIds)
  const nodesRef = useRef(nodes)
  const nodeByIdRef = useRef(new Map(nodes.map((node) => [node.id, node])))
  const edgeStubsRef = useRef(edges)
  const localeRef = useRef(locale)
  const focusRef = useRef(focusId)
  const cameraFocusTargetsRef = useRef<readonly TimelineTarget[]>(cameraFocusTargets ?? [])
  const dimmedNodeIdsRef = useRef(dimmedNodeIds)
  const collectionHighlightIdsRef = useRef(collectionHighlightIds)
  const labelModeRef = useRef(labelMode)
  const lastCameraFocusRef = useRef<string | undefined>(undefined)
  const offscreenSignatureRef = useRef('')
  const [offscreenGroups, setOffscreenGroups] = useState<AtlasOffscreenGroup[]>([])
  const [mobilePresentation, setMobilePresentation] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches
  ))

  activeTypesRef.current = activeTypes
  highlightedTypeRef.current = highlightedType
  selectNodeRef.current = onSelectNode
  selectEdgeRef.current = onSelectEdge
  selectBundleRef.current = onSelectBundle
  clearFocusRef.current = onClearFocus
  requestRegionRef.current = onRequestRegion
  loadedRegionsRef.current = loadedRegionIds
  loadingRegionsRef.current = loadingRegionIds
  nodesRef.current = nodes
  nodeByIdRef.current = new Map(nodes.map((node) => [node.id, node]))
  edgeStubsRef.current = edges
  localeRef.current = locale
  focusRef.current = focusId
  cameraFocusTargetsRef.current = cameraFocusTargets ?? []
  dimmedNodeIdsRef.current = dimmedNodeIds
  collectionHighlightIdsRef.current = collectionHighlightIds
  labelModeRef.current = labelMode

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 760px)')
    const updateMobilePresentation = () => setMobilePresentation(mobileQuery.matches)
    updateMobilePresentation()
    mobileQuery.addEventListener('change', updateMobilePresentation)
    return () => mobileQuery.removeEventListener('change', updateMobilePresentation)
  }, [])

  useImperativeHandle(ref, () => ({
    async exportPng() {
      const cy = cyRef.current
      if (!cy) throw new Error('The atlas is not ready to export.')
      await document.fonts?.ready
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
      return cy.png({ output: 'blob-promise', bg: '#08111C', full: true, maxWidth: 1800, maxHeight: 1800 })
    },
  }), [])

  useEffect(() => {
    if (!containerRef.current) return
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const cameraDuration = reducedMotion ? 0 : 420
    const contourAccent = traditionAccent?.accent ?? '#D6A647'
    const requestRegion = (regionId: AtlasRegionId) => {
      if (!loadedRegionsRef.current.has(regionId) && !loadingRegionsRef.current.has(regionId)) requestRegionRef.current(regionId)
    }
    const placementById = new Map(layout.placements.map((placement) => [placement.id, placement]))
    const atlasNodeImageSrc = (node: GraphNode) => (
      node.media ? `${import.meta.env.BASE_URL}${node.media.src.replace(/^\//, '')}` : ''
    )
    const atlasNodeDefinition = (node: GraphNode): ElementDefinition => {
      const placement = placementById.get(node.id)!
      return {
        data: {
          id: node.id,
          parent: `atlas-region-${placement.regionId}`,
          isRegion: 0,
          isReservation: 0,
          type: node.type,
          atlasTier: placement.displayTier,
          regionId: placement.regionId,
          fullLabel: localizedAtlasLabel(node, localeRef.current),
          displayNodeLabel: localizedAtlasLabel(node, localeRef.current),
          typeIcon: typeIconDataUri(node.type),
          imageSrc: atlasNodeImageSrc(node),
        },
        position: { x: placement.x, y: placement.y },
      }
    }
    const atlasReservationDefinition = (reservation: AtlasLayout['reservations'][number]): ElementDefinition => {
      const label = localeRef.current === 'en'
        ? `${reservation.name.en}\n???`
        : `${reservation.name['zh-Hant']}\n${reservation.name.en}\n???`
      return {
        data: {
          id: `atlas-reservation-${reservation.id}`,
          parent: `atlas-region-${reservation.regionId}`,
          isRegion: 0,
          isReservation: 1,
          type: reservation.type,
          atlasTier: reservation.displayTier,
          regionId: reservation.regionId,
          fullLabel: label,
          displayNodeLabel: label,
          imageSrc: '',
        },
        position: { x: reservation.x, y: reservation.y },
      }
    }
    const elements: ElementDefinition[] = [
      ...layout.regions.map((region) => ({
        data: {
          id: `atlas-region-${region.id}`,
          isRegion: 1,
          regionId: region.id,
          label: '',
        },
      })),
      ...layout.regions.map((region) => ({
        data: {
          id: `atlas-contour-${region.id}`,
          isContour: 1,
          regionId: region.id,
          label: localizedAtlasRegionLabel(region.label, localeRef.current),
          regionContour: region.contour.map((point) => `${point.x} ${point.y}`).join(' '),
          regionTexture: atlasRegionTextureSource(region.id, import.meta.env.BASE_URL),
          contourWidth: Math.round(region.radius.x * 2.08),
          contourHeight: Math.round(region.radius.y * 2.08),
        },
        position: { ...region.center },
      })),
    ]

    const nodeStyles = Object.entries(colors).map(([type, color]) => ({
      selector: `node[type = "${type}"]`,
      style: { 'background-color': color, shape: shapes[type as NodeType] },
    }))
    const edgeStyles = Object.entries(relationshipColors).map(([type, color]) => ({
      selector: `edge[type = "${type}"]`,
      style: { 'line-color': color, 'target-arrow-color': color },
    }))
    const cy = cytoscape({
      container: containerRef.current,
      elements,
      minZoom: 0.1,
      maxZoom: 3.2,
      boxSelectionEnabled: false,
      autoungrabify: true,
      style: [
        {
          selector: 'node[isRegion = 1]',
          style: {
            label: '', shape: 'round-rectangle', 'background-opacity': 0, 'border-width': 0,
            padding: 48, 'overlay-opacity': 0, events: 'no',
          },
        },
        {
          selector: 'node[isContour = 1]',
          style: {
            width: 'data(contourWidth)', height: 'data(contourHeight)', label: 'data(label)', shape: 'polygon',
            'shape-polygon-points': 'data(regionContour)', 'background-opacity': ATLAS_REGION_TINT_OPACITY,
            'background-image': 'data(regionTexture)', 'background-image-opacity': ATLAS_REGION_TEXTURE_OPACITY, 'background-fit': 'cover',
            'background-image-containment': 'inside', 'background-clip': 'node', 'background-image-smoothing': 'yes',
            'background-blacken': 0,
            'background-color': contourAccent, 'border-width': ATLAS_REGION_BORDER_WIDTH, 'border-style': 'dashed', 'border-color': contourAccent,
            'border-opacity': ATLAS_REGION_BORDER_OPACITY, color: '#C6D0D8', 'font-family': 'Noto Sans TC, sans-serif', 'font-size': 22,
            'font-weight': 600, 'text-valign': 'top', 'text-margin-y': -16, 'text-wrap': 'wrap', 'text-max-width': '220px',
            'overlay-opacity': 0, 'z-index': 0, events: 'no',
          },
        },
        {
          selector: 'node[isRegion = 0]',
          style: {
            width: 42, height: 42, label: 'data(displayNodeLabel)', color: '#F4F0E8',
            'font-family': 'Noto Sans TC, sans-serif', 'font-size': 10, 'font-weight': 600,
            'text-valign': 'bottom', 'text-margin-y': 8, 'text-outline-color': '#08111C', 'text-outline-width': 3,
            'text-wrap': 'wrap', 'text-max-width': '112px', 'text-justification': 'center', 'line-height': 1.35,
            'border-width': 1.5, 'border-color': '#F2D793', 'background-opacity': 0.94,
            'background-image': 'data(typeIcon)', 'background-fit': 'contain', 'background-clip': 'node',
            'background-image-containment': 'inside', 'background-width': '25px', 'background-height': '25px',
            'background-position-x': '50%', 'background-position-y': '50%', 'overlay-opacity': 0, 'z-index': 4,
            'transition-property': 'width height opacity text-opacity border-width',
            'transition-duration': reducedMotion ? '0ms' : '180ms', 'transition-timing-function': 'ease-in-out',
          },
        },
        ...nodeStyles,
        {
          selector: 'edge',
          style: {
            width: 0.7, opacity: 0, events: 'no', 'curve-style': 'bezier',
            'target-arrow-shape': (edge: EdgeSingular) => edge.data('directed') ? 'triangle' : 'none',
            'arrow-scale': 0.55, label: 'data(displayLabel)', color: '#E7DCC8', 'font-size': 8,
            'source-label': 'data(sourceDisplayLabel)', 'target-label': 'data(targetDisplayLabel)',
            'source-text-rotation': 'none', 'target-text-rotation': 'none',
            'text-background-color': '#0F1B28', 'text-background-opacity': 0.96, 'text-background-padding': '3px',
            'text-wrap': 'wrap', 'text-max-width': '118px', 'text-rotation': 'autorotate', 'overlay-opacity': 0, 'z-index': 1,
            'transition-property': 'opacity width', 'transition-duration': reducedMotion ? '0ms' : '180ms',
          },
        },
        ...edgeStyles,
        {
          selector: 'edge[kind = "bundle"]',
          style: {
            'line-color': '#9AA7B4',
            'target-arrow-color': '#9AA7B4',
            'target-arrow-shape': 'none',
            'line-style': 'dashed',
          },
        },
        { selector: 'edge.atlas-edge-active', style: { events: 'yes' } },
        { selector: 'edge.atlas-edge-hovered, edge.atlas-edge-selected', style: { label: 'data(label)', width: 2.4, opacity: 1, 'z-index': 16 } },
        { selector: 'edge.atlas-edge-focused', style: { width: 1.8, opacity: 0.9, 'z-index': 12 } },
        { selector: 'edge.atlas-timeline-edge-focused', style: { width: 2.2, opacity: 0.95, 'z-index': 14 } },
        { selector: 'node[atlasTier = 0]', style: { width: 72, height: 72, 'font-size': 12, 'border-width': 3, 'z-index': 8 } },
        { selector: 'node[atlasTier = 1]', style: { width: 54, height: 54, 'font-size': 10, 'z-index': 6 } },
        { selector: '.atlas-hidden', style: { events: 'no' } },
        { selector: '.atlas-marker', style: { events: 'yes' } },
        { selector: '.atlas-filtered', style: { events: 'no' } },
        { selector: '.atlas-focused', style: { 'border-width': 4, 'border-color': '#FFF0B7', 'z-index': 20 } },
        { selector: 'node.atlas-collection-highlight', style: { 'border-width': 3.4, 'border-color': '#7ED1B8', 'z-index': 13 } },
        { selector: 'node.atlas-timeline-focused', style: { 'border-width': 3.2, 'border-color': '#E5B84D', 'z-index': 14 } },
        { selector: 'node.atlas-neighbor', style: { 'border-width': 2.5, 'border-color': '#E9CF8B', 'z-index': 12 } },
        {
          selector: 'node[isReservation = 1]',
          style: {
            shape: 'ellipse', events: 'no', 'background-image': 'none', 'background-color': '#0F1B28',
            'background-opacity': 0.32, 'border-style': 'dashed', 'border-color': '#9AA7B4',
            'border-opacity': 0.62, color: '#9AA7B4', 'font-weight': 500, 'z-index': 2,
          },
        },
        { selector: 'node[isContour = 1][regionId = "primordial"]', style: { 'background-color': '#A98BCE', 'border-color': '#A98BCE' } },
        { selector: 'node[isContour = 1][regionId = "olympus"]', style: { 'background-color': '#D6A647', 'border-color': '#D6A647' } },
        { selector: 'node[isContour = 1][regionId = "mortal"]', style: { 'background-color': '#CDAE79', 'border-color': '#CDAE79' } },
        { selector: 'node[isContour = 1][regionId = "seas"]', style: { 'background-color': '#4D8FCC', 'border-color': '#6D9FCB' } },
        { selector: 'node[isContour = 1][regionId = "underworld"]', style: { 'background-color': '#8C69A8', 'border-color': '#A98BCE' } },
      ] as unknown as cytoscape.StylesheetJson,
      layout: { name: 'preset', fit: false },
    })
    cyRef.current = cy

    const applyPortrait = (graphNode: NodeSingular) => {
      const image = graphNode.data('imageSrc') as string
      if (!image || loadedPortraitsRef.current.has(graphNode.id())) return
      loadedPortraitsRef.current.add(graphNode.id())
      graphNode.style({
        'background-image': `${graphNode.data('typeIcon')} ${image}`,
        'background-fit': 'none cover',
        'background-clip': 'node node',
        'background-image-containment': 'over inside',
        'background-width': '17px 100%',
        'background-height': '17px 100%',
        'background-position-x': '74% 50%',
        'background-position-y': '74% 50%',
      })
    }

    let initialFitCount = 0
    let focusOverviewFitCount = 0
    let focusCameraFitCount = 0
    let presentationRefreshCount = 0
    let focusLabelLayoutCount = 0
    let focusLabelLayoutSignature = ''
    let focusLabelLayoutTimer: number | undefined
    let focusLabelLayoutFrame = 0
    let scriptedCamera = false
    let nodeMaterializationCount = 0

    const syncAtlasNodes = (extent = cy.extent()) => {
      const ratio = cy.zoom() / Math.max(baseZoomRef.current, 0.001)
      const { canonicalNodeIds, reservationNodeIds } = atlasMaterializedElementIds(
        layout,
        nodesRef.current,
        edgeStubsRef.current,
        {
          extent,
          level: atlasLevelForZoomRatio(ratio),
          focusId: focusRef.current,
          focusTargets: cameraFocusTargetsRef.current,
          highlightedType: highlightedTypeRef.current,
          collectionNodeIds: collectionHighlightIdsRef.current,
        },
      )
      const desiredIds = new Set([...canonicalNodeIds, ...reservationNodeIds])
      const existingNodes = cy.nodes('[isRegion = 0]')
      const staleNodes = existingNodes.filter((node) => !desiredIds.has(node.id()))
      const definitions: ElementDefinition[] = []
      let updatedNodeDataCount = 0
      canonicalNodeIds.forEach((id) => {
        const node = nodeByIdRef.current.get(id)
        const existing = cy.getElementById(id).filter('node')
        if (existing.nonempty() && node) {
          const nextFullLabel = localizedAtlasLabel(node, localeRef.current)
          const nextTypeIcon = typeIconDataUri(node.type)
          const nextImageSrc = atlasNodeImageSrc(node)
          const imageChanged = existing.data('imageSrc') !== nextImageSrc
          if (
            existing.data('type') !== node.type
            || existing.data('fullLabel') !== nextFullLabel
            || existing.data('typeIcon') !== nextTypeIcon
            || imageChanged
          ) {
            existing.data({
              type: node.type,
              fullLabel: nextFullLabel,
              displayNodeLabel: nextFullLabel,
              typeIcon: nextTypeIcon,
              imageSrc: nextImageSrc,
            })
            if (imageChanged) {
              loadedPortraitsRef.current.delete(id)
              if (!nextImageSrc) existing.style({
                'background-image': nextTypeIcon,
                'background-fit': 'contain',
                'background-clip': 'node',
                'background-image-containment': 'inside',
                'background-width': '25px',
                'background-height': '25px',
                'background-position-x': '50%',
                'background-position-y': '50%',
              })
            }
            updatedNodeDataCount += 1
          }
          return
        }
        if (node) definitions.push(atlasNodeDefinition(node))
      })
      reservationNodeIds.forEach((id) => {
        if (cy.getElementById(id).nonempty()) return
        const reservation = layout.reservations.find((item) => `atlas-reservation-${item.id}` === id)
        if (reservation) definitions.push(atlasReservationDefinition(reservation))
      })
      if (staleNodes.length || definitions.length) {
        cy.batch(() => {
          staleNodes.forEach((node) => {
            loadedPortraitsRef.current.delete(node.id())
            node.remove()
          })
          if (definitions.length) cy.add(definitions)
        })
        nodeMaterializationCount += 1
      }
      if (containerRef.current) {
        containerRef.current.dataset.materializedNodeCount = String(canonicalNodeIds.size)
        containerRef.current.dataset.materializedReservationCount = String(reservationNodeIds.size)
        containerRef.current.dataset.nodeMaterializationCount = String(nodeMaterializationCount)
        containerRef.current.dataset.nodeDataUpdateCount = String(updatedNodeDataCount)
      }
    }
    cy.scratch('_atlasSyncViewportNodes', syncAtlasNodes)

    const overlapArea = (left: AtlasRect, right: AtlasRect) => {
      const width = Math.max(0, Math.min(left.x2, right.x2) - Math.max(left.x1, right.x1))
      const height = Math.max(0, Math.min(left.y2, right.y2) - Math.max(left.y1, right.y1))
      return width * height
    }

    const emphasizedNeighborIdsFor = (focusedNode: NodeSingular, neighborIds: ReadonlySet<string>, mobile: boolean) => (
      atlasFocusOverviewNeighborIds(
        focusedNode.position(),
        [...neighborIds].flatMap((id) => {
          const neighbor = cy.getElementById(id).filter('node')
          return neighbor.nonempty() ? [{ id, position: neighbor.position() }] : []
        }),
        mobile,
      )
    )

    const timelineFocusNodeIdsForTargets = () => {
      const focusNodeIds = new Set<string>()
      cameraFocusTargetsRef.current.forEach((target) => {
        if (target.kind === 'node') {
          focusNodeIds.add(target.nodeId)
          return
        }
        const graphEdge = cy.getElementById(target.edgeId).filter('edge')
        if (graphEdge.nonempty()) {
          graphEdge.connectedNodes().forEach((node) => { focusNodeIds.add(node.id()) })
          return
        }
        const edge = edgeStubsRef.current.find((item) => item.id === target.edgeId)
        if (!edge) return
        focusNodeIds.add(edge.source)
        focusNodeIds.add(edge.target)
      })
      return focusNodeIds
    }

    const timelineLabelPlacementKey = () => {
      const targetKeys = cameraFocusTargetsRef.current.map(timelineTargetKey)
      return targetKeys.length ? `timeline:${targetKeys.join('|')}` : ''
    }

    const layoutFocusLabels = () => {
      if (cy.destroyed()) return
      if (cy.animated() || scriptedCamera) {
        scheduleFocusLabelLayout()
        return
      }
      const container = containerRef.current
      const focusedNode = focusRef.current ? cy.getElementById(focusRef.current).filter('node') : cy.collection()
      const timelinePlacementKey = timelineLabelPlacementKey()
      const timelineFocusNodeIds = focusedNode.empty() ? timelineFocusNodeIdsForTargets() : new Set<string>()
      if (!container || (focusedNode.empty() && !timelineFocusNodeIds.size)) {
        focusLabelLayoutSignature = ''
        if (container) {
          container.dataset.labelLayoutMode = 'none'
          container.dataset.focusLabelNodeCount = '0'
          container.dataset.timelineLabelNodeCount = '0'
          container.dataset.timelineLabelOverlapArea = '0'
        }
        return
      }
      const timelineLayout = focusedNode.empty()
      const focusedNodeId = timelineLayout ? timelinePlacementKey : focusedNode.id()
      const allNeighborIds = new Set<string>()
      if (!timelineLayout) focusedNode.connectedEdges().connectedNodes().forEach((node) => {
        if (node.id() !== focusedNodeId) allNeighborIds.add(node.id())
      })
      const mobile = window.matchMedia('(max-width: 760px)').matches
      const labelNodeIds = timelineLayout
        ? [...timelineFocusNodeIds].sort()
        : emphasizedNeighborIdsFor(focusedNode.first() as NodeSingular, allNeighborIds, mobile)
      const labelNodes = labelNodeIds
        .map((id) => cy.getElementById(id).filter('node'))
        .filter((node) => node.nonempty() && !node.hasClass('atlas-filtered'))
      if (!labelNodes.length) {
        focusLabelLayoutSignature = ''
        container.dataset.labelLayoutMode = 'none'
        container.dataset.focusLabelNodeCount = '0'
        container.dataset.timelineLabelNodeCount = '0'
        container.dataset.timelineLabelOverlapArea = '0'
        return
      }
      const absoluteZoom = Math.max(cy.zoom(), 0.001)
      const pan = cy.pan()
      const signature = [
        timelineLayout ? 'timeline' : 'focus',
        focusedNodeId,
        mobile ? 'mobile' : 'wide',
        `${container.clientWidth}x${container.clientHeight}`,
        absoluteZoom.toFixed(3),
        `${Math.round(pan.x)},${Math.round(pan.y)}`,
        localeRef.current,
        labelNodes.map((node) => node.id()).join(','),
      ].join('|')
      if (signature === focusLabelLayoutSignature) return

      const focusContextNodes = timelineLayout
        ? labelNodes.reduce((collection, node) => collection.union(node), cy.collection())
        : focusedNode.union(labelNodes)
      const nodeBoxes = focusContextNodes.map((node) => ({
        id: node.id(),
        box: node.renderedBoundingBox({ includeLabels: false, includeOverlays: false }),
      }))
      const occupiedLabels: AtlasRect[] = []
      if (!timelineLayout) {
        const focusLabelBox = focusedNode.renderedBoundingBox({
          includeNodes: false,
          includeEdges: false,
          includeLabels: true,
          includeOverlays: false,
        })
        if (Number.isFinite(focusLabelBox.x1)) occupiedLabels.push(focusLabelBox)
      }
      const renderedPositions = labelNodes.map((node) => node.renderedPosition())
      const focusPosition = timelineLayout && renderedPositions.length
        ? {
          x: renderedPositions.reduce((sum, position) => sum + position.x, 0) / renderedPositions.length,
          y: renderedPositions.reduce((sum, position) => sum + position.y, 0) / renderedPositions.length,
        }
        : focusedNode.renderedPosition()
      labelNodes.sort((left, right) => {
        const leftPosition = left.renderedPosition()
        const rightPosition = right.renderedPosition()
        const leftDensity = nodeBoxes.filter(({ id, box }) => id !== left.id() && Math.hypot(box.x1 - leftPosition.x, box.y1 - leftPosition.y) < 110).length
        const rightDensity = nodeBoxes.filter(({ id, box }) => id !== right.id() && Math.hypot(box.x1 - rightPosition.x, box.y1 - rightPosition.y) < 110).length
        return rightDensity - leftDensity || left.id().localeCompare(right.id())
      })

      const placements: Array<{ node: NodeSingular; candidate: AtlasLabelCandidate }> = []
      let timelineLabelOverlapArea = 0
      labelNodes.forEach((node) => {
        const position = node.renderedPosition()
        const dx = position.x - focusPosition.x
        const dy = position.y - focusPosition.y
        const horizontalFirst = Math.abs(dx) > Math.abs(dy) * 0.82
        const preferred: AtlasLabelCandidate = horizontalFirst
          ? dx >= 0
            ? { textHalign: 'right', textValign: 'center', textJustification: 'left', offsetX: 10, offsetY: 0 }
            : { textHalign: 'left', textValign: 'center', textJustification: 'right', offsetX: -10, offsetY: 0 }
            : dy >= 0
            ? { textHalign: 'center', textValign: 'bottom', textJustification: 'center', offsetX: 0, offsetY: 10 }
            : { textHalign: 'center', textValign: 'top', textJustification: 'center', offsetX: 0, offsetY: -10 }
        const candidates: AtlasLabelCandidate[] = [preferred]
        ;[10, 26, 42, 58, 78].forEach((offset) => {
          candidates.push(
            { textHalign: 'right', textValign: 'center', textJustification: 'left', offsetX: offset, offsetY: 0 },
            { textHalign: 'left', textValign: 'center', textJustification: 'right', offsetX: -offset, offsetY: 0 },
            { textHalign: 'center', textValign: 'bottom', textJustification: 'center', offsetX: 0, offsetY: offset },
            { textHalign: 'center', textValign: 'top', textJustification: 'center', offsetX: 0, offsetY: -offset },
          )
          if (timelineLayout) candidates.push(
            { textHalign: 'right', textValign: 'bottom', textJustification: 'left', offsetX: offset, offsetY: offset },
            { textHalign: 'right', textValign: 'top', textJustification: 'left', offsetX: offset, offsetY: -offset },
            { textHalign: 'left', textValign: 'bottom', textJustification: 'right', offsetX: -offset, offsetY: offset },
            { textHalign: 'left', textValign: 'top', textJustification: 'right', offsetX: -offset, offsetY: -offset },
          )
        })
        const nodeBox = nodeBoxes.find((entry) => entry.id === node.id())!.box
        const measuredLabelBox = node.renderedBoundingBox({
          includeNodes: false,
          includeEdges: false,
          includeLabels: true,
          includeOverlays: false,
        })
        const labelSize = {
          width: Math.max(1, measuredLabelBox.w),
          height: Math.max(1, measuredLabelBox.h),
        }
        let bestCandidate = candidates[0]
        let bestBox = atlasProjectedLabelBox(nodeBox, labelSize, bestCandidate)
        let bestScore = Number.POSITIVE_INFINITY
        let bestLabelOverlap = 0
        candidates.forEach((candidate) => {
          const box = atlasProjectedLabelBox(nodeBox, labelSize, candidate)
          const boundaryOverflow = Math.max(0, 8 - box.x1)
            + Math.max(0, box.x2 - (container.clientWidth - 8))
            + Math.max(0, 54 - box.y1)
            + Math.max(0, box.y2 - (container.clientHeight - 56))
          const labelOverlap = occupiedLabels.reduce((total, occupied) => total + overlapArea(box, occupied), 0)
          const nodeOverlap = nodeBoxes.reduce((total, entry) => entry.id === node.id() ? total : total + overlapArea(box, entry.box), 0)
          const score = boundaryOverflow * 1000 + labelOverlap * (timelineLayout ? 16 : 8) + nodeOverlap * (timelineLayout ? 4 : 2) + Math.hypot(candidate.offsetX, candidate.offsetY) * 0.4
          if (score < bestScore) {
            bestScore = score
            bestCandidate = candidate
            bestBox = box
            bestLabelOverlap = labelOverlap
          }
        })
        placements.push({ node: node.first() as NodeSingular, candidate: bestCandidate })
        if (timelineLayout) timelineLabelOverlapArea += bestLabelOverlap
        occupiedLabels.push(bestBox)
      })

      cy.batch(() => {
        placements.forEach(({ node, candidate }) => {
          node.style({
            'text-halign': candidate.textHalign,
            'text-valign': candidate.textValign,
            'text-justification': candidate.textJustification,
            'text-margin-x': candidate.offsetX / absoluteZoom,
            'text-margin-y': candidate.offsetY / absoluteZoom,
          })
          node.data('focusLabelPlacement', { focusId: focusedNodeId, ...candidate } satisfies AtlasStoredLabelPlacement)
        })
      })
      focusLabelLayoutSignature = signature
      focusLabelLayoutCount += 1
      container.dataset.focusLabelLayoutCount = String(focusLabelLayoutCount)
      container.dataset.labelLayoutMode = timelineLayout ? 'timeline' : 'focus'
      container.dataset.focusLabelNodeCount = timelineLayout ? '0' : String(labelNodes.length)
      container.dataset.timelineLabelNodeCount = timelineLayout ? String(labelNodes.length) : '0'
      container.dataset.timelineLabelOverlapArea = timelineLayout ? String(Math.round(timelineLabelOverlapArea)) : '0'
    }

    function scheduleFocusLabelLayout(invalidate = false) {
      if (invalidate) focusLabelLayoutSignature = ''
      if (focusLabelLayoutTimer) window.clearTimeout(focusLabelLayoutTimer)
      window.cancelAnimationFrame(focusLabelLayoutFrame)
      focusLabelLayoutTimer = window.setTimeout(() => {
        focusLabelLayoutFrame = window.requestAnimationFrame(layoutFocusLabels)
      }, FOCUS_LABEL_LAYOUT_DELAY_MS)
    }

    const materializeAtlasEdges = (extent: { x1: number; y1: number; x2: number; y2: number }) => {
      const rawFocusId = focusRef.current
      const focusTargetKeys = new Set(cameraFocusTargetsRef.current.map(timelineTargetKey))
      const focusedNode = rawFocusId ? cy.getElementById(rawFocusId).filter('node') : cy.collection()
      const focusedNodeId = focusedNode.nonempty() ? focusedNode.id() : undefined
      const directNeighborIds = new Set<string>()
      if (focusedNodeId) edgeStubsRef.current.forEach((edge) => {
        if (edge.source === focusedNodeId) directNeighborIds.add(edge.target)
        else if (edge.target === focusedNodeId) directNeighborIds.add(edge.source)
      })
      const closureNodeIds = new Set(focusedNodeId ? [focusedNodeId, ...directNeighborIds] : [])
      const intersectsViewport = (edge: AtlasDisplayEdge) => {
        const source = cy.getElementById(edge.source).filter('node')
        const target = cy.getElementById(edge.target).filter('node')
        if (source.empty() || target.empty()) return false
        const sourcePosition = source.position()
        const targetPosition = target.position()
        const padding = 80
        return Math.max(sourcePosition.x, targetPosition.x) >= extent.x1 - padding
          && Math.min(sourcePosition.x, targetPosition.x) <= extent.x2 + padding
          && Math.max(sourcePosition.y, targetPosition.y) >= extent.y1 - padding
          && Math.min(sourcePosition.y, targetPosition.y) <= extent.y2 + padding
      }
      const desired = edgeStubsRef.current.filter((edge) => {
        if (focusTargetKeys.has(`edge:${edge.id}`)) return true
        if (focusedNodeId) {
          const otherId = edge.source === focusedNodeId ? edge.target : edge.target === focusedNodeId ? edge.source : undefined
          const directFocusEdge = Boolean(otherId)
          return directFocusEdge || (closureNodeIds.has(edge.source) && closureNodeIds.has(edge.target))
        }
        if (rawFocusId) return edge.id === rawFocusId
        return intersectsViewport(edge)
      })
      const desiredIds = new Set(desired.map((edge) => edge.id))
      const stale = cy.edges().filter((edge) => !desiredIds.has(edge.id()))
      const missing = desired.filter((edge) => cy.getElementById(edge.id).empty())
      if (!stale.length && !missing.length) return
      cy.batch(() => {
        stale.remove()
        missing.forEach((edge) => {
          cy.add({
            data: {
              ...edge,
              label: localize(edge.label, localeRef.current),
              displayLabel: '',
              sourceDisplayLabel: '',
              targetDisplayLabel: '',
            },
          }).scratch('_atlasOffscreenPresentation', true)
        })
      })
    }

    const refreshPresentation = () => {
      if (cy.destroyed()) return
      presentationRefreshCount += 1
      const ratio = cy.zoom() / Math.max(baseZoomRef.current, 0.001)
      const level = atlasLevelForZoomRatio(ratio)
      const extent = cy.extent()
      const mobileFocusOverview = window.matchMedia('(max-width: 760px)').matches
      syncAtlasNodes(extent)
      materializeAtlasEdges(extent)
      const focusedNode = focusRef.current ? cy.getElementById(focusRef.current).filter('node') : cy.collection()
      const focusedEdge = focusRef.current ? cy.getElementById(focusRef.current).filter('edge') : cy.collection()
      const focusedNodeId = focusedNode.empty() ? undefined : focusedNode.id()
      const selectedEdgeId = focusedEdge.empty() ? undefined : focusedEdge.id()
      const timelineFocusNodeIds = new Set<string>()
      const timelineFocusEdgeIds = new Set<string>()
      const currentTimelineLabelPlacementKey = timelineLabelPlacementKey()
      cameraFocusTargetsRef.current.forEach((target) => {
        if (target.kind === 'node') {
          timelineFocusNodeIds.add(target.nodeId)
          return
        }
        timelineFocusEdgeIds.add(target.edgeId)
        const edge = edgeStubsRef.current.find((item) => item.id === target.edgeId)
        if (!edge) return
        timelineFocusNodeIds.add(edge.source)
        timelineFocusNodeIds.add(edge.target)
      })
      const denseTimelineLabelScene = !focusedNodeId && timelineFocusNodeIds.size >= TIMELINE_DENSE_LABEL_NODE_THRESHOLD
      const timelineRegionIds = new Set<AtlasRegionId>()
      timelineFocusNodeIds.forEach((nodeId) => {
        const focusNode = cy.getElementById(nodeId).filter('node')
        const regionId = focusNode.data('regionId') as AtlasRegionId | undefined
        if (regionId) timelineRegionIds.add(regionId)
      })
      const neighborIds = new Set<string>()
      if (focusedNodeId) edgeStubsRef.current.forEach((edge) => {
        if (edge.source === focusedNodeId) neighborIds.add(edge.target)
        else if (edge.target === focusedNodeId) neighborIds.add(edge.source)
      })
      if (selectedEdgeId) focusedEdge.connectedNodes().forEach((node) => { neighborIds.add(node.id()) })
      if (focusedNodeId) neighborIds.delete(focusedNodeId)
      const emphasizedNeighborIds = focusedNodeId
        ? new Set(emphasizedNeighborIdsFor(focusedNode.first() as NodeSingular, neighborIds, mobileFocusOverview))
        : neighborIds
      const {
        featured: featuredProgress,
        detail: detailProgress,
        featuredLabel: featuredLabelProgress,
        detailLabel: detailLabelProgress,
        region: regionFade,
      } = atlasLayerProgressForZoom(ratio)
      let visibleCount = 0
      let visibleMarkerCount = 0
      let visibleLabelCount = 0
      let collectionDimmedNodeCount = 0
      let collectionDimmedEdgeCount = 0
      const absoluteZoom = Math.max(cy.zoom(), 0.001)
      const labelFontSize = Math.max(7, Math.min(38, 11 / absoluteZoom))
      const labelMargin = Math.max(3, Math.min(24, 8 / absoluteZoom))
      const labelOutline = Math.max(1.2, Math.min(9, 2.4 / absoluteZoom))
      const labelMaxWidth = Math.max(72, Math.min(320, 112 / absoluteZoom))
      const regionHeadingMetrics = atlasRegionHeadingMetrics(absoluteZoom, mobileFocusOverview)
      const timelineRegionEmphasis = timelineRegionIds.size > 1
      const regionHeadingOpacityFloor = level === 'detail' && !timelineRegionEmphasis ? 0.52 : timelineRegionEmphasis ? 0.86 : 0.82
      const regionHeadingTextOpacity = Math.max(regionFade, regionHeadingOpacityFloor)
      const minimumTimelineRenderedSize = mobileFocusOverview ? 38 : 42
      const maximumFocusRenderedSize = mobileFocusOverview ? 64 : 74
      let adaptiveFocusRenderedSize = maximumFocusRenderedSize
      if (focusedNodeId) emphasizedNeighborIds.forEach((neighborId) => {
        const neighbor = cy.getElementById(neighborId).filter('node')
        if (neighbor.empty()) return
        const distance = Math.hypot(
          neighbor.position('x') - focusedNode.position('x'),
          neighbor.position('y') - focusedNode.position('y'),
        ) * absoluteZoom
        adaptiveFocusRenderedSize = Math.min(adaptiveFocusRenderedSize, distance * 2 - 14)
      })
      adaptiveFocusRenderedSize = Math.max(
        focusedNodeId && timelineFocusNodeIds.has(focusedNodeId) ? minimumTimelineRenderedSize : 18,
        adaptiveFocusRenderedSize,
      )
      cy.startBatch()
      try {
        cy.nodes('[isContour = 1]').style({
          'background-opacity': ATLAS_REGION_TINT_OPACITY * regionFade,
          'background-image-opacity': ATLAS_REGION_TEXTURE_OPACITY * regionFade,
          'border-opacity': ATLAS_REGION_BORDER_OPACITY * regionFade,
          'font-size': regionHeadingMetrics.fontSize,
          'text-opacity': regionHeadingTextOpacity,
          'text-margin-y': regionHeadingMetrics.labelMargin,
          'text-outline-color': '#08111C',
          'text-outline-width': regionHeadingMetrics.outlineWidth,
          'text-max-width': regionHeadingMetrics.labelWidth,
          'text-background-color': '#08111C',
          'text-background-opacity': timelineRegionEmphasis ? 0.62 : level === 'world' ? 0.42 : 0.32,
          'text-background-padding': regionHeadingMetrics.backgroundPadding,
        })
        cy.nodes('[isRegion = 0]').forEach((graphNode) => {
        const type = graphNode.data('type') as NodeType
        const tier = Number(graphNode.data('atlasTier'))
        const isReservation = Boolean(graphNode.data('isReservation'))
        const collectionNodeIds = collectionHighlightIdsRef.current
        const collectionSpotlightActive = Boolean(collectionNodeIds?.size)
        const isCollectionMember = Boolean(collectionNodeIds?.has(graphNode.id()))
        const collectionProminent = collectionSpotlightActive && isCollectionMember
        const filtered = !activeTypesRef.current.has(type) && !isCollectionMember
        const visibility = tier === 0 ? 1 : tier === 1 ? featuredProgress : detailProgress
        const labelOpacity = tier === 0 ? 1 : tier === 1 ? featuredLabelProgress : detailLabelProgress
        const typeHighlight = atlasTypeHighlightProgress(type, highlightedTypeRef.current, isReservation, ratio)
        const isExactFocus = graphNode.id() === focusedNodeId
        const isTimelineFocus = timelineFocusNodeIds.has(graphNode.id())
        const isDirectNeighbor = neighborIds.has(graphNode.id()) && graphNode.id() !== focusedNodeId
        const isEmphasizedNeighbor = emphasizedNeighborIds.has(graphNode.id()) && graphNode.id() !== focusedNodeId
        const focusOverviewRole: AtlasFocusOverviewRole | undefined = isExactFocus
          ? 'focus'
          : isEmphasizedNeighbor && !collectionSpotlightActive ? 'neighbor' : undefined
        let focusMetrics = focusOverviewRole
          ? atlasFocusOverviewMetrics(focusOverviewRole, absoluteZoom, mobileFocusOverview)
          : isTimelineFocus
            ? atlasTimelineFocusMetrics(absoluteZoom, mobileFocusOverview)
            : collectionProminent ? atlasCollectionSpotlightMetrics(absoluteZoom, mobileFocusOverview) : undefined
        if (focusMetrics && isExactFocus) focusMetrics = {
          ...focusMetrics,
          size: adaptiveFocusRenderedSize / absoluteZoom,
          renderedSize: adaptiveFocusRenderedSize,
        }
        if (focusMetrics && focusOverviewRole === 'neighbor' && focusedNodeId) {
          const defaultRenderedSize = focusMetrics.renderedSize
          let collisionLimitedSize = defaultRenderedSize
          emphasizedNeighborIds.forEach((otherId) => {
            if (otherId === graphNode.id()) return
            const other = cy.getElementById(otherId).filter('node')
            if (other.empty()) return
            const distance = Math.hypot(
              other.position('x') - graphNode.position('x'),
              other.position('y') - graphNode.position('y'),
            ) * absoluteZoom
            collisionLimitedSize = Math.min(collisionLimitedSize, distance - 6)
          })
          const focusDistance = Math.hypot(
            focusedNode.position('x') - graphNode.position('x'),
            focusedNode.position('y') - graphNode.position('y'),
          ) * absoluteZoom
          collisionLimitedSize = Math.min(collisionLimitedSize, focusDistance * 2 - adaptiveFocusRenderedSize - 8)
          const renderedSize = Math.max(isTimelineFocus ? minimumTimelineRenderedSize : 8, collisionLimitedSize)
          focusMetrics = {
            ...focusMetrics,
            size: renderedSize / absoluteZoom,
            renderedSize,
          }
        }
        const baseLabel = String(graphNode.data('fullLabel'))
        if (graphNode.data('displayNodeLabel') !== baseLabel) graphNode.data('displayNodeLabel', baseLabel)
        const storedLabelPlacement = graphNode.data('focusLabelPlacement') as AtlasStoredLabelPlacement | undefined
        let labelPlacement = isEmphasizedNeighbor && focusedNodeId
          ? storedLabelPlacement?.focusId === focusedNodeId
            ? {
              textHalign: storedLabelPlacement.textHalign,
              textValign: storedLabelPlacement.textValign,
              textJustification: storedLabelPlacement.textJustification,
              marginX: storedLabelPlacement.offsetX / absoluteZoom,
              marginY: storedLabelPlacement.offsetY / absoluteZoom,
            }
            : atlasFocusLabelPlacement(focusedNode.position(), graphNode.position(), absoluteZoom)
          : undefined
        if (!labelPlacement && isTimelineFocus && currentTimelineLabelPlacementKey && storedLabelPlacement?.focusId === currentTimelineLabelPlacementKey) {
          labelPlacement = {
            textHalign: storedLabelPlacement.textHalign,
            textValign: storedLabelPlacement.textValign,
            textJustification: storedLabelPlacement.textJustification,
            marginX: storedLabelPlacement.offsetX / absoluteZoom,
            marginY: storedLabelPlacement.offsetY / absoluteZoom,
          }
        }
        if (labelPlacement && containerRef.current) {
          const renderedPosition = graphNode.renderedPosition()
          const viewportWidth = containerRef.current.clientWidth
          const viewportHeight = containerRef.current.clientHeight
          const boundaryMargin = 10 / absoluteZoom
          if (renderedPosition.x > viewportWidth - 142) labelPlacement = {
            textHalign: 'left', textValign: 'center', textJustification: 'right', marginX: -boundaryMargin, marginY: 0,
          }
          else if (renderedPosition.x < 142) labelPlacement = {
            textHalign: 'right', textValign: 'center', textJustification: 'left', marginX: boundaryMargin, marginY: 0,
          }
          else if (renderedPosition.y < 72) labelPlacement = {
            textHalign: 'center', textValign: 'bottom', textJustification: 'center', marginX: 0, marginY: boundaryMargin,
          }
          else if (renderedPosition.y > viewportHeight - 82) labelPlacement = {
            textHalign: 'center', textValign: 'top', textJustification: 'center', marginX: 0, marginY: -boundaryMargin,
          }
        }
        const effectiveVisibility = focusOverviewRole || isTimelineFocus || isCollectionMember ? 1 : Math.max(visibility, typeHighlight.visibilityFloor)
        const effectiveLabelOpacity = focusOverviewRole || isTimelineFocus || isCollectionMember ? 1 : Math.max(labelOpacity, typeHighlight.labelFloor)
        const visible = effectiveVisibility > 0.08
        const marker = !focusOverviewRole && tier === 2 && detailLabelProgress < 0.45
        const sizeProgress = tier === 0 ? progressBetween(ratio, 1, 2.2) : effectiveVisibility
        const startSize = tier === 0 ? 72 : tier === 1 ? 27 : 14
        const endSize = tier === 0 ? 60 : tier === 1 ? 54 : 42
        graphNode.toggleClass('atlas-filtered', filtered)
        graphNode.toggleClass('atlas-hidden', !visible)
        graphNode.toggleClass('atlas-marker', visible && marker)
        const inFocusedContext = !focusRef.current || isExactFocus || isDirectNeighbor || isTimelineFocus
        const focusOpacity = isExactFocus ? 1 : inFocusedContext ? 1 : 0.14
        const timelineOpacity = dimmedNodeIdsRef.current?.has(graphNode.id()) && !isExactFocus && !isTimelineFocus ? 0.38 : 1
        const collectionContextOpacity = collectionSpotlightActive && !isCollectionMember && !isExactFocus && !isTimelineFocus ? 0.12 : 1
        const collectionLabelOpacity = collectionSpotlightActive && !isCollectionMember && !isExactFocus && !isTimelineFocus ? 0.06 : 1
        const contextProminent = isExactFocus || isTimelineFocus || collectionProminent || (!collectionSpotlightActive && isDirectNeighbor)
        if (collectionSpotlightActive && collectionContextOpacity < 1) collectionDimmedNodeCount += 1
        const nextLabelOpacity = filtered && !isExactFocus
          ? 0
          : isTimelineFocus || isCollectionMember ? Math.max(0.92, effectiveLabelOpacity)
            : focusOverviewRole ? 1 : effectiveLabelOpacity * (typeHighlight.matches ? 1 : inFocusedContext ? typeHighlight.contextOpacity : 0.28) * collectionLabelOpacity
        graphNode.toggleClass('atlas-focused', graphNode.id() === focusedNodeId)
        graphNode.toggleClass('atlas-collection-highlight', isCollectionMember && graphNode.id() !== focusedNodeId && !isTimelineFocus)
        graphNode.toggleClass('atlas-timeline-focused', isTimelineFocus && graphNode.id() !== focusedNodeId)
        graphNode.toggleClass('atlas-neighbor', isEmphasizedNeighbor)
        graphNode.style({
          width: focusMetrics?.size ?? interpolate(startSize, endSize, sizeProgress),
          height: focusMetrics?.size ?? interpolate(startSize, endSize, sizeProgress),
          opacity: (filtered && !isExactFocus ? Math.min(0.08, effectiveVisibility) : effectiveVisibility * (contextProminent ? 1 : focusOpacity * typeHighlight.contextOpacity)) * timelineOpacity * collectionContextOpacity,
          'text-opacity': nextLabelOpacity,
          'font-size': focusMetrics?.fontSize ?? labelFontSize,
          'text-halign': labelPlacement?.textHalign ?? 'center',
          'text-valign': labelPlacement?.textValign ?? 'bottom',
          'text-justification': labelPlacement?.textJustification ?? 'center',
          'text-margin-x': labelPlacement?.marginX ?? 0,
          'text-margin-y': labelPlacement?.marginY ?? focusMetrics?.labelMargin ?? labelMargin,
          'text-outline-width': focusMetrics?.outlineWidth ?? labelOutline,
          'text-max-width': focusMetrics?.labelWidth ?? labelMaxWidth,
          'text-background-color': '#0F1B28',
          'text-background-opacity': focusOverviewRole ? 0.78 : isTimelineFocus ? 0.64 : isCollectionMember ? 0.58 : 0,
          'text-background-padding': focusOverviewRole || isTimelineFocus || isCollectionMember ? 3 / absoluteZoom : 0,
          'border-width': focusMetrics?.borderWidth ?? (isTimelineFocus ? 3.2 : isCollectionMember ? 3.4 : interpolate(0.5, tier === 0 ? 3 : typeHighlight.matches ? 2.8 : 1.5, effectiveVisibility)),
        })
        if (visible && !filtered && isInsideExtent(graphNode, extent)) {
          visibleMarkerCount += 1
          if (nextLabelOpacity > 0.08) visibleLabelCount += 1
        }
        if (effectiveVisibility > 0.12 && (!filtered || isExactFocus) && !isReservation && isInsideExtent(graphNode, extent)) {
          visibleCount += 1
          if (focusOverviewRole || isTimelineFocus || collectionProminent || tier === 0 || typeHighlight.matches || (tier === 1 && featuredProgress > 0.16) || (tier === 2 && detailProgress > 0.42)) applyPortrait(graphNode)
        }
        })

      const relationshipProgress = atlasRelationshipProgressForZoom(ratio)
      cy.edges().forEach((edge) => {
        const source = edge.source()
        const target = edge.target()
        const isSelected = edge.id() === selectedEdgeId
        if (!isSelected && !edgeIntersectsExtent(edge, extent)) {
          if (!edge.scratch('_atlasOffscreenPresentation')) {
            edge.removeClass('atlas-edge-active atlas-edge-focused atlas-edge-selected')
            edge.style({ opacity: 0 })
            edge.data({
              displayLabel: '',
              sourceDisplayLabel: '',
              targetDisplayLabel: '',
            })
            edge.scratch('_atlasOffscreenPresentation', true)
          }
          return
        }
        edge.scratch('_atlasOffscreenPresentation', false)
        const sourceRegion = source.data('regionId') as AtlasRegionId
        const targetRegion = target.data('regionId') as AtlasRegionId
        const crossRegion = sourceRegion !== targetRegion
        const crossRegionReady = !crossRegion
          || (loadedRegionsRef.current.has(sourceRegion) && loadedRegionsRef.current.has(targetRegion))
        const filtered = source.hasClass('atlas-filtered') || target.hasClass('atlas-filtered')
        const endpointsVisible = !source.hasClass('atlas-hidden') && !target.hasClass('atlas-hidden')
        const isTimelineEdgeFocus = timelineFocusEdgeIds.has(edge.id())
        const collectionNodeIds = collectionHighlightIdsRef.current
        const collectionSpotlightActive = Boolean(collectionNodeIds?.size)
        const sourceCollectionMember = Boolean(collectionNodeIds?.has(source.id()))
        const targetCollectionMember = Boolean(collectionNodeIds?.has(target.id()))
        const collectionEdge = sourceCollectionMember && targetCollectionMember
        const focusDrivenEdge = Boolean(focusedNodeId && (edge.data('source') === focusedNodeId || edge.data('target') === focusedNodeId))
        const collectionSuppressesFocusEdge = collectionSpotlightActive && focusDrivenEdge && !collectionEdge && !isSelected && !isTimelineEdgeFocus
        const isFocused = isSelected || isTimelineEdgeFocus || (focusDrivenEdge && !collectionSuppressesFocusEdge)
        const focusDim = focusRef.current && !isFocused ? 0.12 : 1
        const highlighted = highlightedTypeRef.current
        const touchesHighlightedType = Boolean(highlighted && (
          (!source.data('isReservation') && source.data('type') === highlighted)
          || (!target.data('isReservation') && target.data('type') === highlighted)
        ))
        const typeEmphasis = highlighted ? touchesHighlightedType ? 1.7 : 0.16 : 1
        const collectionEdgeContextOpacity = collectionSpotlightActive && !isSelected
          ? collectionEdge ? 0.55 : 0.04
          : 1
        if (collectionSpotlightActive && !collectionEdge && !isSelected) collectionDimmedEdgeCount += 1
        const opacity = filtered || (!endpointsVisible && !isSelected) || (!crossRegionReady && !isFocused)
          ? 0
          : isFocused ? collectionSpotlightActive && collectionEdge && !isSelected ? 0.62 : 0.9 : Math.min(0.58, relationshipProgress * (crossRegion ? 0.16 : 0.22) * focusDim * typeEmphasis * collectionEdgeContextOpacity)
        const focusOverviewEdge = Boolean(focusedNodeId && isFocused)
        const screenSpaceEdge = focusOverviewEdge || isTimelineEdgeFocus
        edge.toggleClass('atlas-edge-active', opacity > 0.08)
        edge.toggleClass('atlas-edge-focused', isFocused && !isSelected)
        edge.toggleClass('atlas-timeline-edge-focused', isTimelineEdgeFocus && !isSelected)
        edge.toggleClass('atlas-edge-selected', isSelected)
        edge.style({
          opacity,
          width: screenSpaceEdge ? (isTimelineEdgeFocus ? 2.4 : 2.2) / absoluteZoom : isFocused ? 1.8 : 0.7 + relationshipProgress * (touchesHighlightedType ? 0.9 : 0.45),
          'font-size': screenSpaceEdge ? (mobileFocusOverview ? 10 : 10.5) / absoluteZoom : 8,
          'text-background-padding': screenSpaceEdge ? 3 / absoluteZoom : 3,
          'text-max-width': screenSpaceEdge ? 126 / absoluteZoom : 118,
          'source-text-offset': 0,
          'target-text-offset': 0,
          'source-text-margin-y': 0,
          'target-text-margin-y': 0,
          'arrow-scale': screenSpaceEdge ? 0.78 : 0.55,
        })
        if (edge.data('sourceDisplayLabel')) edge.data('sourceDisplayLabel', '')
        if (edge.data('targetDisplayLabel')) edge.data('targetDisplayLabel', '')
        const displayLabel = isSelected || (isTimelineEdgeFocus && !denseTimelineLabelScene) ? edge.data('label') : ''
        if (edge.data('displayLabel') !== displayLabel) edge.data('displayLabel', displayLabel)
      })
      const activeEdges = cy.edges('.atlas-edge-active')
      const showPersistentLabels = labelModeRef.current === 'always'
        && relationshipProgress > 0.82
        && !focusRef.current
        && activeEdges.length <= 18
      if (showPersistentLabels) activeEdges.forEach((edge) => {
        if (edge.data('displayLabel') !== edge.data('label')) edge.data('displayLabel', edge.data('label'))
      })
      } finally {
        cy.endBatch()
      }
      scheduleFocusLabelLayout()
      const nextOffscreenGroups: AtlasOffscreenGroup[] = []
      if (focusedNodeId && containerRef.current) {
        const width = containerRef.current.clientWidth
        const height = containerRef.current.clientHeight
        const origin = focusedNode.renderedPosition()
        const bySide = new Map<AtlasEdgeSide, Array<AtlasOffscreenLink & { x: number; y: number }>>()
        const seenNodes = new Set<string>()
        edgeStubsRef.current.forEach((edge) => {
          const otherId = edge.source === focusedNodeId ? edge.target : edge.target === focusedNodeId ? edge.source : undefined
          if (!otherId) return
          const other = cy.getElementById(otherId).filter('node')
          if (other.empty()) return
          if (seenNodes.has(other.id()) || other.hasClass('atlas-filtered')) return
          seenNodes.add(other.id())
          const position = other.renderedPosition()
          const inside = position.x >= 42 && position.x <= width - 42 && position.y >= 58 && position.y <= height - 58
          if (inside) return
          const projection = projectAtlasEdgeLink(origin, position, width, height)
          const item = {
            nodeId: other.id(),
            label: String(other.data('fullLabel')).replace('\n', ' · '),
            relationship: localize(edge.label, localeRef.current),
            x: projection.x,
            y: projection.y,
          }
          bySide.set(projection.side, [...(bySide.get(projection.side) ?? []), item])
        })
        const sideOrder: AtlasEdgeSide[] = ['top', 'right', 'bottom', 'left']
        sideOrder.forEach((side) => {
          const items = bySide.get(side)?.sort((left, right) => left.label.localeCompare(right.label, localeRef.current))
          if (!items?.length) return
          const averageX = items.reduce((total, item) => total + item.x, 0) / items.length
          const averageY = items.reduce((total, item) => total + item.y, 0) / items.length
          nextOffscreenGroups.push({
            side,
            x: Math.max(92, Math.min(width - 92, averageX)),
            y: Math.max(82, Math.min(height - (storyContext ? mobileFocusOverview ? 98 : 130 : 82), averageY)),
            items: items.map(({ nodeId, label, relationship }) => ({ nodeId, label, relationship })),
          })
        })
      }
      const nextOffscreenSignature = JSON.stringify(nextOffscreenGroups)
      if (nextOffscreenSignature !== offscreenSignatureRef.current) {
        offscreenSignatureRef.current = nextOffscreenSignature
        setOffscreenGroups(nextOffscreenGroups)
      }
      if (containerRef.current) {
        const pan = cy.pan()
        const probeRegion = layout.regions.find((region) => cy.getElementById(`atlas-region-${region.id}`).nonempty()) ?? layout.regions[0]
        let probeX = 0
        let probeY = 0
        if (probeRegion) {
          const probeNodes = cy.getElementById(`atlas-region-${probeRegion.id}`).children()
          const probeMargin = 58 / absoluteZoom
          let probePoint = { ...probeRegion.center }
          let probeClearance = -1
          for (let row = -4; row <= 4; row += 1) {
            for (let column = -4; column <= 4; column += 1) {
              const normalizedX = column / 5
              const normalizedY = row / 5
              if (normalizedX ** 2 + normalizedY ** 2 > 0.78) continue
              const point = {
                x: probeRegion.center.x + normalizedX * probeRegion.radius.x,
                y: probeRegion.center.y + normalizedY * probeRegion.radius.y,
              }
              if (point.x < extent.x1 + probeMargin || point.x > extent.x2 - probeMargin
                || point.y < extent.y1 + probeMargin || point.y > extent.y2 - probeMargin) continue
              const distances = probeNodes.map((node) => Math.hypot(point.x - node.position('x'), point.y - node.position('y')))
              const clearance = distances.length ? Math.min(...distances) : Number.POSITIVE_INFINITY
              if (clearance > probeClearance) {
                probePoint = point
                probeClearance = clearance
              }
            }
          }
          probeX = probePoint.x * absoluteZoom + pan.x
          probeY = probePoint.y * absoluteZoom + pan.y
        }
        containerRef.current.dataset.atlasLevel = level
        containerRef.current.dataset.visibleNodeCount = String(visibleCount)
        containerRef.current.dataset.visibleMarkerCount = String(visibleMarkerCount)
        containerRef.current.dataset.visibleLabelCount = String(visibleLabelCount)
        containerRef.current.dataset.suppressedLabelCount = '0'
        containerRef.current.dataset.loadedPortraits = String(loadedPortraitsRef.current.size)
        containerRef.current.dataset.materializedEdgeCount = String(cy.edges().length)
        containerRef.current.dataset.visibleEdgeCount = String(cy.edges('.atlas-edge-active').length)
        containerRef.current.dataset.collectionSpotlight = collectionHighlightIdsRef.current?.size ? 'true' : 'false'
        containerRef.current.dataset.collectionDimmedNodeCount = String(collectionDimmedNodeCount)
        containerRef.current.dataset.collectionDimmedEdgeCount = String(collectionDimmedEdgeCount)
        containerRef.current.dataset.panX = String(Math.round(pan.x))
        containerRef.current.dataset.panY = String(Math.round(pan.y))
        containerRef.current.dataset.regionPanProbeX = String(Math.round(probeX))
        containerRef.current.dataset.regionPanProbeY = String(Math.round(probeY))
        containerRef.current.dataset.cameraFitCount = String(initialFitCount)
        containerRef.current.dataset.focusOverviewFitCount = String(focusOverviewFitCount)
        containerRef.current.dataset.focusCameraFitCount = String(focusCameraFitCount)
        containerRef.current.dataset.focusRelatedNodeCount = String(focusedNodeId ? neighborIds.size : 0)
        containerRef.current.dataset.presentationRefreshCount = String(presentationRefreshCount)
        containerRef.current.dataset.focusNodeRenderedSize = focusedNodeId ? String(mobileFocusOverview ? 64 : 74) : '0'
        containerRef.current.dataset.focusNeighborRenderedSize = focusedNodeId ? String(mobileFocusOverview ? 36 : 40) : '0'
        containerRef.current.dataset.focusRelationshipRenderedFontSize = focusedNodeId ? String(mobileFocusOverview ? 10 : 11) : '0'
        const timelineFocusNodes = [...timelineFocusNodeIds]
          .map((nodeId) => cy.getElementById(nodeId).filter('node'))
          .filter((candidate) => candidate.nonempty())
        const firstTimelineFocusNode = timelineFocusNodes[0] ?? cy.collection()
        const timelineFocusNodeMeasuredWidths = timelineFocusNodes.map((focusNode) => focusNode.renderedBoundingBox({ includeLabels: false, includeOverlays: false }).w)
        const timelineFocusNodeMissingImageIds = timelineFocusNodes
          .filter((focusNode) => !focusNode.data('imageSrc'))
          .map((focusNode) => focusNode.id())
          .sort()
        const timelineFocusPortraitAppliedIds = timelineFocusNodes
          .filter((focusNode) => loadedPortraitsRef.current.has(focusNode.id()))
          .map((focusNode) => focusNode.id())
          .sort()
        const collectionFocusNodes = [...(collectionHighlightIdsRef.current ?? new Set<string>())]
          .map((nodeId) => cy.getElementById(nodeId).filter('node'))
          .filter((candidate) => candidate.nonempty())
        collectionFocusNodes.forEach((focusNode) => applyPortrait(focusNode.first() as NodeSingular))
        const collectionFocusNodeMeasuredWidths = collectionFocusNodes
          .map((focusNode) => focusNode.renderedBoundingBox({ includeLabels: false, includeOverlays: false }).w)
        const collectionFocusNodeMissingImageIds = collectionFocusNodes
          .filter((focusNode) => !focusNode.data('imageSrc'))
          .map((focusNode) => focusNode.id())
          .sort()
        const collectionFocusPortraitAppliedIds = collectionFocusNodes
          .filter((focusNode) => loadedPortraitsRef.current.has(focusNode.id()))
          .map((focusNode) => focusNode.id())
          .sort()
        containerRef.current.dataset.timelineFocusNodeRenderedSize = timelineFocusNodeIds.size ? String(mobileFocusOverview ? 42 : 46) : '0'
        containerRef.current.dataset.collectionFocusNodeRenderedSize = collectionHighlightIdsRef.current?.size ? String(mobileFocusOverview ? 42 : 46) : '0'
        containerRef.current.dataset.collectionFocusNodeMeasuredMin = collectionFocusNodeMeasuredWidths.length
          ? Math.min(...collectionFocusNodeMeasuredWidths).toFixed(1)
          : '0'
        containerRef.current.dataset.collectionFocusNodeImageMissingIds = collectionFocusNodeMissingImageIds.join(',')
        containerRef.current.dataset.collectionFocusPortraitAppliedCount = String(collectionFocusPortraitAppliedIds.length)
        containerRef.current.dataset.collectionFocusPortraitAppliedIds = collectionFocusPortraitAppliedIds.join(',')
        containerRef.current.dataset.collectionHighlightCount = String(collectionHighlightIdsRef.current?.size ?? 0)
        containerRef.current.dataset.collectionHighlightIds = [...(collectionHighlightIdsRef.current ?? new Set<string>())].sort().join(',')
        containerRef.current.dataset.timelineFocusNodeMeasuredWidth = firstTimelineFocusNode.nonempty()
          ? firstTimelineFocusNode.renderedBoundingBox({ includeLabels: false, includeOverlays: false }).w.toFixed(1)
          : '0'
        containerRef.current.dataset.timelineFocusNodeMeasuredMin = timelineFocusNodeMeasuredWidths.length
          ? Math.min(...timelineFocusNodeMeasuredWidths).toFixed(1)
          : '0'
        containerRef.current.dataset.timelineFocusNodeIds = [...timelineFocusNodeIds].sort().join(',')
        containerRef.current.dataset.timelineFocusNodeImageMissingIds = timelineFocusNodeMissingImageIds.join(',')
        containerRef.current.dataset.timelineFocusPortraitAppliedIds = timelineFocusPortraitAppliedIds.join(',')
        containerRef.current.dataset.timelineDenseLabelScene = denseTimelineLabelScene ? 'true' : 'false'
        containerRef.current.dataset.timelineEdgeLabelSuppressedCount = String(denseTimelineLabelScene ? timelineFocusEdgeIds.size : 0)
        containerRef.current.dataset.timelineRegionCount = String(timelineRegionIds.size)
        containerRef.current.dataset.regionLabelRenderedSize = String(regionHeadingMetrics.renderedFontSize)
        containerRef.current.dataset.regionLabelOpacity = regionHeadingTextOpacity.toFixed(2)
        containerRef.current.dataset.timelineRegionLabelRenderedSize = timelineRegionEmphasis ? String(regionHeadingMetrics.renderedFontSize) : '0'
        containerRef.current.dataset.focusNodeMeasuredWidth = focusedNodeId
          ? focusedNode.renderedBoundingBox({ includeLabels: false, includeOverlays: false }).w.toFixed(1)
          : '0'
        const firstNeighbor = focusedNodeId ? cy.getElementById([...emphasizedNeighborIds][0]).filter('node') : cy.collection()
        containerRef.current.dataset.focusNeighborMeasuredWidth = firstNeighbor.nonempty()
          ? firstNeighbor.renderedBoundingBox({ includeLabels: false, includeOverlays: false }).w.toFixed(1)
          : '0'
        containerRef.current.dataset.zoom = cy.zoom().toFixed(4)
      }
    }

    const focusCamera = (id: string, duration = cameraDuration) => {
      const focusNode = cy.getElementById(id).filter('node')
      const focusEdge = cy.getElementById(id).filter('edge')
      if (!focusNode.empty()) {
        focusCameraFitCount += 1
        const mobile = window.matchMedia('(max-width: 760px)').matches
        const directEdges = focusNode.connectedEdges().filter((edge) => {
          const other = edge.source().id() === focusNode.id() ? edge.target() : edge.source()
          return activeTypesRef.current.has(other.data('type') as NodeType)
        })
        const directNeighborById = new Map<string, NodeSingular>()
        directEdges.forEach((edge) => {
          const other = edge.source().id() === focusNode.id() ? edge.target() : edge.source()
          directNeighborById.set(other.id(), other)
        })
        const overviewNeighborIds = new Set(atlasFocusOverviewNeighborIds(
          focusNode.position(),
          [...directNeighborById.values()].map((neighbor) => ({ id: neighbor.id(), position: neighbor.position() })),
          mobile,
        ))
        const overviewEdges = mobile
          ? directEdges.filter((edge) => {
            const relationship = edge as EdgeSingular
            const other = relationship.source().id() === focusNode.id() ? relationship.target() : relationship.source()
            return overviewNeighborIds.has(other.id())
          })
          : directEdges
        const overviewNodes = focusNode.union(overviewEdges.connectedNodes())
        const overviewElements = overviewNodes.union(overviewEdges)
        const padding = mobile ? 62 : atlasFocusOverviewPadding(cy.width())
        focusOverviewFitCount += 1
        cy.stop()
        cy.resize()
        if (storyContext) {
          const reservedBottom = mobile ? 92 : 112
          const availableHeight = Math.max(1, cy.height() - reservedBottom)
          const bounds = overviewElements.boundingBox({ includeLabels: false, includeOverlays: false })
          const zoom = Math.max(cy.minZoom(), Math.min(
            cy.maxZoom(),
            Math.max(1, cy.width() - padding * 2) / Math.max(1, bounds.w),
            Math.max(1, availableHeight - padding * 2) / Math.max(1, bounds.h),
          ))
          animateCamera({
            zoom,
            pan: {
              x: cy.width() / 2 - zoom * (bounds.x1 + bounds.w / 2),
              y: availableHeight / 2 - zoom * (bounds.y1 + bounds.h / 2),
            },
          }, duration, false)
        } else {
          animateCamera({
            fit: { eles: overviewElements, padding },
          }, duration, false)
        }
      } else if (!focusEdge.empty()) {
        focusCameraFitCount += 1
        animateCamera({
          fit: { eles: focusEdge.connectedNodes().union(focusEdge), padding: window.matchMedia('(max-width: 760px)').matches ? 62 : 150 },
        }, duration, false)
      }
    }
    cy.scratch('_atlasFocusCamera', focusCamera)

    const focusTimelineCamera = (targets: readonly TimelineTarget[], duration = cameraDuration) => {
      const targetElements = targets.reduce((collection, target) => {
        if (target.kind === 'node') {
          return collection.union(cy.getElementById(target.nodeId).filter('node'))
        }
        const graphEdge = cy.getElementById(target.edgeId).filter('edge')
        if (graphEdge.nonempty()) return collection.union(graphEdge.connectedNodes()).union(graphEdge)
        const edge = edgeStubsRef.current.find((candidate) => candidate.id === target.edgeId)
        if (!edge) return collection
        return collection
          .union(cy.getElementById(edge.source).filter('node'))
          .union(cy.getElementById(edge.target).filter('node'))
      }, cy.collection())
      if (targetElements.empty()) return
      focusCameraFitCount += 1
      cy.stop()
      cy.resize()
      animateCamera({
        fit: {
          eles: targetElements,
          padding: window.matchMedia('(max-width: 760px)').matches ? 64 : 112,
        },
      }, duration, false)
    }
    cy.scratch('_atlasTimelineFocusCamera', focusTimelineCamera)

    const focusCollectionCamera = (ids: readonly string[], duration = cameraDuration) => {
      const targetElements = ids.reduce((collection, id) => (
        collection.union(cy.getElementById(id).filter('node'))
      ), cy.collection())
      if (targetElements.empty()) return
      focusCameraFitCount += 1
      cy.stop()
      cy.resize()
      animateCamera({
        fit: {
          eles: targetElements,
          padding: window.matchMedia('(max-width: 760px)').matches ? 58 : 120,
        },
      }, duration, false)
    }
    cy.scratch('_atlasCollectionCamera', focusCollectionCamera)

    const atlasOverviewViewport = () => {
      const elements = cy.nodes('[isContour = 1]')
      const bounds = elements.boundingBox({ includeLabels: false, includeOverlays: false })
      if (!bounds.w || !bounds.h) return undefined
      const baseZoom = Math.max(cy.minZoom(), Math.min(cy.maxZoom(), cy.width() / bounds.w, cy.height() / bounds.h))
      const zoom = Math.min(cy.maxZoom(), baseZoom * DEFAULT_ATLAS_ZOOM_RATIO)
      return {
        baseZoom,
        zoom,
        pan: {
          x: cy.width() / 2 - zoom * (bounds.x1 + bounds.w / 2),
          y: cy.height() / 2 - zoom * (bounds.y1 + bounds.h / 2),
        },
      }
    }

    let viewportFrame = 0
    let viewportRefreshTimer: number | undefined
    let lastViewportPresentationAt = 0
    let regionRequestTimer: number | undefined
    let cameraTimer: number | undefined
    let viewportRegionRequestsEnabled = false
    const initialFit = () => {
      if (cy.destroyed()) return
      initialFitCount += 1
      cy.resize()
      const overview = atlasOverviewViewport()
      if (overview) {
        baseZoomRef.current = overview.baseZoom
        scriptedCamera = true
        cy.viewport({ zoom: overview.zoom, pan: overview.pan })
        cameraTimer = window.setTimeout(() => { scriptedCamera = false }, 40)
      }
      refreshPresentation()
    }
    window.requestAnimationFrame(initialFit)

    const requestVisibleRegions = () => {
      if (cy.destroyed() || scriptedCamera || !viewportRegionRequestsEnabled) return
      const ratio = cy.zoom() / Math.max(baseZoomRef.current, 0.001)
      if (atlasLevelForZoomRatio(ratio) === 'world') return
      const extent = cy.extent()
      layout.regions.forEach((region) => {
        const intersects = extent.x1 <= region.center.x + region.radius.x
          && extent.x2 >= region.center.x - region.radius.x
          && extent.y1 <= region.center.y + region.radius.y
          && extent.y2 >= region.center.y - region.radius.y
        if (intersects) requestRegion(region.id)
      })
    }
    const enableViewportRegionRequests = () => {
      viewportRegionRequestsEnabled = true
    }
    containerRef.current.addEventListener('pointerdown', enableViewportRegionRequests, { passive: true })
    containerRef.current.addEventListener('wheel', enableViewportRegionRequests, { passive: true })
    const animateCamera = (options: cytoscape.AnimateOptions, duration: number, requestAfter = true) => {
      scriptedCamera = true
      if (cameraTimer) window.clearTimeout(cameraTimer)
      cy.animate({ ...options, duration, easing: 'ease-in-out-cubic' })
      cameraTimer = window.setTimeout(() => {
        scriptedCamera = false
        refreshPresentation()
        if (requestAfter) requestVisibleRegions()
      }, duration + 40)
    }
    const scheduleRefresh = () => {
      if (scriptedCamera) return
      window.cancelAnimationFrame(viewportFrame)
      if (viewportRefreshTimer) window.clearTimeout(viewportRefreshTimer)
      const scheduleFrame = () => {
        viewportFrame = window.requestAnimationFrame(() => {
          lastViewportPresentationAt = performance.now()
          refreshPresentation()
        })
      }
      const remaining = VIEWPORT_PRESENTATION_INTERVAL_MS - (performance.now() - lastViewportPresentationAt)
      if (remaining <= 0) scheduleFrame()
      else viewportRefreshTimer = window.setTimeout(scheduleFrame, remaining)
      if (regionRequestTimer) window.clearTimeout(regionRequestTimer)
      regionRequestTimer = window.setTimeout(requestVisibleRegions, 150)
    }
    cy.on('zoom pan', scheduleRefresh)

    const focusRegion = (regionId: AtlasRegionId) => {
      viewportRegionRequestsEnabled = true
      clearFocusRef.current()
      requestRegion(regionId)
      const regionCenter = cy.getElementById(`atlas-contour-${regionId}`)
      if (!regionCenter.empty()) animateCamera({
        center: { eles: regionCenter },
        zoom: Math.min(cy.maxZoom(), baseZoomRef.current * ATLAS_DETAIL_ZOOM_RATIO),
      }, cameraDuration, false)
    }
    cy.on('tap', 'node[isRegion = 0]', (event) => {
      if (event.target.data('isReservation') || event.target.hasClass('atlas-hidden') || event.target.hasClass('atlas-filtered')) return
      event.target.addClass('atlas-focused')
      selectNodeRef.current(event.target.id())
    })
    cy.on('tap', 'edge', (event) => {
      if (!event.target.hasClass('atlas-edge-active')) return
      const edge = edgeStubsRef.current.find((candidate) => candidate.id === event.target.id())
      if (edge?.kind === 'bundle') selectBundleRef.current?.(edge)
      else selectEdgeRef.current(event.target.id())
    })
    cy.on('mouseover', 'edge.atlas-edge-active', (event) => event.target.addClass('atlas-edge-hovered'))
    cy.on('mouseout', 'edge', (event) => restoreAtlasEdgeAfterHover(event.target, refreshPresentation))
    cy.on('tap', (event) => {
      if (event.target !== cy) return
      const position = event.position
      const region = layout.regions
        .filter((candidate) => {
          const normalizedX = (position.x - candidate.center.x) / candidate.radius.x
          const normalizedY = (position.y - candidate.center.y) / candidate.radius.y
          return normalizedX ** 2 + normalizedY ** 2 <= 1.08
        })
        .sort((left, right) => (left.radius.x * left.radius.y) - (right.radius.x * right.radius.y))[0]
      if (region) focusRegion(region.id)
      else clearFocusRef.current()
    })

    let resizeFrame = 0
    const resizeObserver = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(() => {
      if (cy.destroyed()) return
      cy.resize()
      window.cancelAnimationFrame(resizeFrame)
      resizeFrame = window.requestAnimationFrame(refreshPresentation)
    })
    resizeObserver?.observe(containerRef.current)

    const zoomToRegion = (event: Event) => {
      const regionId = (event.currentTarget as HTMLElement).dataset.regionId as AtlasRegionId
      focusRegion(regionId)
    }
    const reset = () => {
      clearFocusRef.current()
      const overview = atlasOverviewViewport()
      if (overview) {
        baseZoomRef.current = overview.baseZoom
        animateCamera({ zoom: overview.zoom, pan: overview.pan }, cameraDuration, false)
      }
    }
    const zoomIn = () => {
      viewportRegionRequestsEnabled = true
      animateCamera({ zoom: Math.min(cy.maxZoom(), cy.zoom() * 1.32) }, reducedMotion ? 0 : 260)
    }
    const zoomOut = () => {
      viewportRegionRequestsEnabled = true
      animateCamera({ zoom: Math.max(cy.minZoom(), cy.zoom() / 1.32) }, reducedMotion ? 0 : 260)
    }
    const atlasRoot = containerRef.current.parentElement!
    const regionButtons = [...atlasRoot.querySelectorAll<HTMLElement>('[data-atlas-region-button]')]
    const resetButton = atlasRoot.querySelector<HTMLElement>('[data-atlas-reset]')
    const zoomInButton = atlasRoot.querySelector<HTMLElement>('[data-atlas-zoom-in]')
    const zoomOutButton = atlasRoot.querySelector<HTMLElement>('[data-atlas-zoom-out]')
    regionButtons.forEach((button) => button.addEventListener('click', zoomToRegion))
    resetButton?.addEventListener('click', reset)
    zoomInButton?.addEventListener('click', zoomIn)
    zoomOutButton?.addEventListener('click', zoomOut)

    return () => {
      window.cancelAnimationFrame(viewportFrame)
      window.cancelAnimationFrame(focusLabelLayoutFrame)
      if (viewportRefreshTimer) window.clearTimeout(viewportRefreshTimer)
      if (focusLabelLayoutTimer) window.clearTimeout(focusLabelLayoutTimer)
      if (regionRequestTimer) window.clearTimeout(regionRequestTimer)
      if (cameraTimer) window.clearTimeout(cameraTimer)
      resizeObserver?.disconnect()
      containerRef.current?.removeEventListener('pointerdown', enableViewportRegionRequests)
      containerRef.current?.removeEventListener('wheel', enableViewportRegionRequests)
      window.cancelAnimationFrame(resizeFrame)
      regionButtons.forEach((button) => button.removeEventListener('click', zoomToRegion))
      resetButton?.removeEventListener('click', reset)
      zoomInButton?.removeEventListener('click', zoomIn)
      zoomOutButton?.removeEventListener('click', zoomOut)
      cy.destroy()
      cyRef.current = null
      lastCameraFocusRef.current = undefined
    }
  }, [layout, storyContext, traditionAccent?.accent])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    const syncViewportNodes = cy.scratch('_atlasSyncViewportNodes') as (() => void) | undefined
    syncViewportNodes?.()
    const edgeById = new Map(edges.map((edge) => [edge.id, edge]))
    cy.batch(() => {
      cy.edges().forEach((existing) => {
        const edge = edgeById.get(existing.id())
        if (!edge) {
          existing.remove()
          return
        }
        const label = localize(edge.label, locale)
        if (existing.data('source') !== edge.source
          || existing.data('target') !== edge.target
          || existing.data('type') !== edge.type
          || existing.data('directed') !== edge.directed
          || existing.data('featured') !== edge.featured
          || existing.data('label') !== label) {
          existing.data({
            ...edge,
            label,
            displayLabel: '',
            sourceDisplayLabel: '',
            targetDisplayLabel: '',
          })
          existing.scratch('_atlasOffscreenPresentation', false)
        }
      })
    })
    cy.emit('zoom')
  }, [edges, locale])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    const syncViewportNodes = cy.scratch('_atlasSyncViewportNodes') as (() => void) | undefined
    syncViewportNodes?.()
    const cameraTargets = cameraFocusTargets ?? []
    const cameraFocusKey = cameraTargets.map(timelineTargetKey).join('|')
    const collectionFocusIds = [...(collectionHighlightIds ?? new Set<string>())].sort()
    const collectionFocusKey = !focusId && !cameraFocusKey && collectionFocusIds.length
      ? `collection:${collectionFocusRequest}:${collectionFocusIds.join(',')}`
      : ''
    if (!focusId) {
      if (!cameraFocusKey && !collectionFocusKey) {
        lastCameraFocusRef.current = undefined
      }
      cy.emit('zoom')
    }
    cy.emit('zoom')
    if (!cameraFollow) {
      lastCameraFocusRef.current = undefined
      return
    }
    const nextCameraKey = cameraFocusKey || (focusId ? `focus:${focusId}` : collectionFocusKey)
    if (!nextCameraKey) return
    if (lastCameraFocusRef.current === nextCameraKey) return
    lastCameraFocusRef.current = nextCameraKey
    window.requestAnimationFrame(() => {
      if (cy.destroyed()) return
      const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 420
      if (cameraTargets.length > 0) {
        syncViewportNodes?.()
        cy.emit('zoom')
        const focusTimelineCamera = cy.scratch('_atlasTimelineFocusCamera') as ((targets: readonly TimelineTarget[], duration?: number) => void) | undefined
        focusTimelineCamera?.(cameraTargets, duration)
        return
      }
      if (focusId) {
        const focusCamera = cy.scratch('_atlasFocusCamera') as ((id: string, duration?: number) => void) | undefined
        focusCamera?.(focusId, duration)
        return
      }
      if (collectionFocusIds.length > 0) {
        syncViewportNodes?.()
        cy.emit('zoom')
        const focusCollectionCamera = cy.scratch('_atlasCollectionCamera') as ((ids: readonly string[], duration?: number) => void) | undefined
        focusCollectionCamera?.(collectionFocusIds, duration)
      }
    })
  }, [cameraFocusTargets, cameraFollow, collectionFocusRequest, collectionHighlightIds, edges, focusId])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy || !focusId || !relatedTypeFilter) return
    const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300
    window.requestAnimationFrame(() => {
      if (cy.destroyed()) return
      const focusCamera = cy.scratch('_atlasFocusCamera') as ((id: string, duration?: number) => void) | undefined
      focusCamera?.(focusId, duration)
    })
  }, [focusId, relatedTypeFilter])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    const syncViewportNodes = cy.scratch('_atlasSyncViewportNodes') as (() => void) | undefined
    syncViewportNodes?.()
    cy.batch(() => {
      layout.regions.forEach((region) => {
        cy.getElementById(`atlas-contour-${region.id}`).data('label', localizedAtlasRegionLabel(region.label, locale))
      })
      cy.nodes('[isRegion = 0][isReservation = 0]').forEach((graphNode) => {
        const node = nodeByIdRef.current.get(graphNode.id())
        if (!node) return
        const imageSrc = node.media ? `${import.meta.env.BASE_URL}${node.media.src.replace(/^\//, '')}` : ''
        const nodeLabel = localizedAtlasLabel(node, locale)
        if (graphNode.data('imageSrc') !== imageSrc) loadedPortraitsRef.current.delete(node.id)
        graphNode.data({
          fullLabel: nodeLabel,
          displayNodeLabel: nodeLabel,
          imageSrc,
        })
      })
      layout.reservations.forEach((reservation) => {
        const reservationLabel = locale === 'en'
          ? `${reservation.name.en}\n???`
          : `${reservation.name['zh-Hant']}\n${reservation.name.en}\n？？？`
        cy.getElementById(`atlas-reservation-${reservation.id}`).data({
          fullLabel: reservationLabel,
          displayNodeLabel: reservationLabel,
        })
      })
    })
    cy.emit('zoom')
  }, [layout.regions, locale, nodes])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    const syncViewportNodes = cy.scratch('_atlasSyncViewportNodes') as (() => void) | undefined
    syncViewportNodes?.()
    cy.emit('zoom')
  }, [activeTypes, highlightedType, labelMode, collectionHighlightIds])

  return <div
    className={`atlas-map${storyContext ? ' atlas-map-story-context' : ''}`}
    data-testid="atlas-map"
    data-node-count={nodes.length}
    data-reservation-count={layout.reservations.length}
    data-highlighted-type={highlightedType ?? ''}
    data-related-type-filter={relatedTypeFilter ?? ''}
    data-edge-count={edges.length}
    data-semantic-edge-count={edges.filter((edge) => edge.kind === 'semantic').length}
    data-bundle-count={edges.filter((edge) => edge.kind === 'bundle').length}
    data-focus-id={focusId ?? ''}
    data-collection-highlight-count={collectionHighlightIds?.size ?? 0}
    data-collection-highlight-ids={collectionHighlightIds ? [...collectionHighlightIds].sort().join(',') : ''}
    data-collection-spotlight={collectionHighlightIds?.size ? 'true' : 'false'}
    data-collection-dimmed-node-count="0"
    data-collection-dimmed-edge-count="0"
    data-collection-focus-node-rendered-size={collectionHighlightIds?.size ? String(mobilePresentation ? 42 : 46) : '0'}
    data-collection-focus-node-measured-min="0"
    data-camera-follow={cameraFollow ? 'true' : 'false'}
    data-continuous-focus="true"
    data-focus-overview="fit-one-hop"
    data-focus-overview-screen-space="true"
    data-focus-label-content="names-only"
    data-relationship-colour-key="visible"
    data-type-icons="true"
    data-type-badge-size="17"
    data-layout-version={ATLAS_LAYOUT_VERSION}
    data-tradition-accent={traditionAccent?.accent ?? '#D6A647'}
    data-region-shape="irregular-contours"
    data-region-backgrounds="generated-bitmaps-feathered"
    data-loaded-regions={[...loadedRegionIds].sort().join(',')}
    data-nodes-draggable="false"
    data-section-pan-enabled="true"
    data-offscreen-link-count={offscreenGroups.reduce((total, group) => total + group.items.length, 0)}
    data-story-context={storyContext ? 'true' : 'false'}
  >
    <div ref={containerRef} className="atlas-canvas" role="img" aria-label={locale === 'en'
      ? storyContext
        ? 'Across-myths focus on the fixed mythology atlas. Related nodes remain in their canonical atlas positions.'
        : 'Fixed mythology atlas. Zoom into a region or use search to explore every node.'
      : storyContext
        ? '正在神話地圖查看跨神話關係，相關項目會留在原有位置。'
        : '神話地圖。你可以放大故事區域，或使用搜尋探索所有項目。'} />
    {offscreenGroups.length > 0 && <nav className="atlas-edge-links" data-testid="atlas-edge-links" aria-label={locale === 'en' ? 'Related items outside the current view' : '目前畫面外的相關項目'}>
      {offscreenGroups.map((group) => <details key={`${focusId}-${group.side}`} className={`atlas-edge-link side-${group.side}`} style={{ left: group.x, top: group.y }}>
        <summary aria-label={locale === 'en' ? `${group.items.length} related items off screen toward the ${group.side}` : `${group.items.length} 個相關項目位於畫面${group.side === 'top' ? '上方' : group.side === 'right' ? '右方' : group.side === 'bottom' ? '下方' : '左方'}`}>
          <span aria-hidden="true">{group.side === 'top' ? '↑' : group.side === 'right' ? '→' : group.side === 'bottom' ? '↓' : '←'}</span><strong>{group.items.length}</strong><small>{locale === 'en' ? 'related' : '相關'}</small>
        </summary>
        <div>{group.items.map((item) => <button type="button" key={item.nodeId} data-node-id={item.nodeId} onClick={() => onSelectNode(item.nodeId)}><strong>{item.label}</strong><small>{item.relationship}</small></button>)}</div>
      </details>)}
    </nav>}
    {!storyContext && <nav className="atlas-region-nav" aria-label={locale === 'en' ? 'Atlas regions' : '神話地圖區域'}>
      <button type="button" data-atlas-reset>{locale === 'en' ? 'Whole atlas' : '完整地圖'}</button>
      {layout.regions.map((region) => <button
        key={region.id}
        type="button"
        className={loadedRegionIds.has(region.id) ? 'loaded' : loadingRegionIds.has(region.id) ? 'loading' : ''}
        data-atlas-region-button
        data-region-id={region.id}
        aria-busy={loadingRegionIds.has(region.id)}
      >{localize(region.label, locale)}</button>)}
    </nav>}
    <div className="atlas-zoom-controls" aria-label={locale === 'en' ? 'Map zoom' : '地圖縮放'}>
      <button type="button" data-atlas-zoom-in aria-label={locale === 'en' ? 'Zoom in' : '放大'}>＋</button>
      <button type="button" data-atlas-zoom-out aria-label={locale === 'en' ? 'Zoom out' : '縮小'}>−</button>
    </div>
    {loadingRegionIds.size > 0 && <div className="atlas-tile-status" role="status">
      <i aria-hidden="true" />{locale === 'en' ? 'Loading map detail…' : '正在載入地圖細節…'}
    </div>}
  </div>
})
